import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CustomToolbar from "../global/CustomToolbar";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import GetAppIcon from "@material-ui/icons/GetApp";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import RefreshIcon from "@material-ui/icons/Refresh";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import PublishIcon from "@material-ui/icons/Publish";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ScheduleIcon from "@material-ui/icons/Schedule";
import PhotoCameraIcon from "@material-ui/icons/PhotoCamera";
import UndoIcon from "@material-ui/icons/Undo";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import InlineDatePicker from "../global/InlineDatePicker";
import BreakdownDialog from "./BreakdownDialog";
import ConfirmDialog from "./ConfirmDialog";
import Spinner from "./Spinner";
import {
  MONTHS, MONTHS_SHORT, money0, money2, weekRange, weeksInMonth, toISO, newItemId, sum,
  computeShare, scrollPtToTop,
} from "./utils";
import {
  MAX_RECEIPTS, RECEIPT_MAX_BYTES, compressReceipt, isHandheld, newReceiptId,
} from "./receiptImage";

// True if a location moved any money this week (profit, delivery, or ATM).
// Used to decide whether an ARCHIVED location is worth showing: archived +
// no value → hidden; archived + real value → still shown. Active locations
// bypass this check entirely.
function locationHasWeekValue(l) {
  if ((Number(l.netProfit) || 0) !== 0) return true;
  const d = l.delivery || {};
  if ((Number(d.cash) || 0) || (Number(d.managerZelle) || 0) ||
      (Number(d.atmReceipts) || 0) || (Number(d.atmAdv) || 0)) return true;
  const a = l.atm || {};
  if ((Number(a.cash) || 0) || (Number(a.adv) || 0) ||
      (Number(a.profit) || 0) || (Number(a.didntUse) || 0)) return true;
  return false;
}

/* ═══════════════════════════════════════════════════════════
   Tiny shared UI atoms
   ══════════════════════════════════════════════════════════ */

function PTToolbar({ title, onBack, actions }) {
  const headerButtons = [];
  if (onBack) {
    headerButtons.push({
      icon: <KeyboardBackspaceIcon />,
      title: "Back",
      handler: onBack,
    });
  }
  (actions || []).forEach((a) => headerButtons.push(a));
  return <CustomToolbar title={title} headerButtons={headerButtons} />;
}

// ─── Delivery PDF ──────────────────────────────────────────
// Mirrors LizAccounting.downloadDeliveryPDF — generates a styled
// "How It Was Delivered" table and triggers a browser download.
function downloadDeliveryPdf({ locations, week, year }) {
  const rows = (locations || []).map((l) => {
    const d = l.delivery || {};
    const cash = Number(d.cash) || 0;
    const zelle = Number(d.managerZelle) || 0;
    const atmReceipts = Number(d.atmReceipts) || 0;
    const atmAdv = Number(d.atmAdv) || 0;
    return {
      locationName: l.name,
      cash, zelle, atmReceipts, atmAdv,
      totalDelivered: cash + zelle + atmReceipts + atmAdv,
    };
  });

  const doc = new jsPDF();
  const fmt = (val) =>
    `$${Number(val).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  doc.setFontSize(18);
  doc.setTextColor(40, 40, 40);
  doc.text("How It Was Delivered", 14, 20);

  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  const weekLabel = week ? `${week.id} · ${week.label}, ${year}` : "Delivery Summary";
  doc.text(weekLabel, 14, 28);

  const totals = rows.reduce(
    (acc, r) => ({
      cash: acc.cash + r.cash,
      zelle: acc.zelle + r.zelle,
      atmReceipts: acc.atmReceipts + r.atmReceipts,
      atmAdv: acc.atmAdv + r.atmAdv,
      totalDelivered: acc.totalDelivered + r.totalDelivered,
    }),
    { cash: 0, zelle: 0, atmReceipts: 0, atmAdv: 0, totalDelivered: 0 },
  );

  autoTable(doc, {
    startY: 35,
    head: [["Location", "Cash", "Zelle", "ATM Receipts", "ATM Adv", "Total Delivered"]],
    body: [
      ...rows.map((r) => [
        r.locationName,
        fmt(r.cash),
        fmt(r.zelle),
        fmt(r.atmReceipts),
        fmt(r.atmAdv),
        fmt(r.totalDelivered),
      ]),
      [
        { content: "TOTAL", styles: { fontStyle: "bold" } },
        { content: fmt(totals.cash), styles: { fontStyle: "bold" } },
        { content: fmt(totals.zelle), styles: { fontStyle: "bold" } },
        { content: fmt(totals.atmReceipts), styles: { fontStyle: "bold" } },
        { content: fmt(totals.atmAdv), styles: { fontStyle: "bold" } },
        { content: fmt(totals.totalDelivered), styles: { fontStyle: "bold" } },
      ],
    ],
    headStyles: { fillColor: [45, 90, 61], textColor: 255, fontStyle: "bold", fontSize: 10 },
    bodyStyles: { fontSize: 10, textColor: [40, 40, 40] },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    styles: { cellPadding: 5, lineColor: [200, 200, 220], lineWidth: 0.2 },
    columnStyles: { 0: { cellWidth: 40 } },
  });

  const safeWeek = week ? `${year}_W${week.weekNumber}` : "summary";
  doc.save(`delivery_${safeWeek}.pdf`);
}

function SectionCard({
  title, color, items, sign, totalOverride, totalLabel = "Total", defaultOpen = true,
  editable, onAdd, onRemove, onEditItem, renderRowClick, children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [newDesc, setNewDesc] = useState("");
  const [newAmt, setNewAmt] = useState("");

  const total = totalOverride ?? sum(items, (x) => x.amount);
  const signClass = sign === "-" ? "var(--pt-red)" : "var(--pt-green)";

  const doAdd = () => {
    if (!newDesc.trim()) return;
    const v = Number(newAmt);
    if (!Number.isFinite(v) || v === 0) return;
    if (onAdd) onAdd({ description: newDesc.trim(), amount: Math.abs(v), isManual: true });
    setNewDesc("");
    setNewAmt("");
  };

  return (
    <div className="pt-sec">
      <button className="pt-sec-head" onClick={() => setOpen(!open)}>
        <span className="title" style={{ color }}>{title}</span>
        <span style={{ color: "var(--pt-muted-2)", fontSize: 11 }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="pt-sec-body">
          {items.length === 0 && !editable && (
            <div style={{ padding: "6px 0", color: "var(--pt-muted-2)", fontSize: 11 }}>
              No items.
            </div>
          )}
          {items.map((it, idx) => {
            const isNds = !it.isManual;
            if (editable && !isNds) {
              return (
                <div key={it._id || idx} className="pt-edit-item">
                  <input
                    value={it.description || ""}
                    onChange={(e) => onEditItem && onEditItem(idx, { description: e.target.value })}
                    placeholder="Description"
                  />
                  <input
                    className="amount"
                    type="number"
                    value={it.amount ?? 0}
                    onChange={(e) => onEditItem && onEditItem(idx, { amount: Number(e.target.value) || 0 })}
                  />
                  <button className="del" onClick={() => onRemove && onRemove(idx)} title="Remove">✕</button>
                </div>
              );
            }
            const clickable = !!renderRowClick && !!it._breakdown;
            return (
              <div
                key={it._id || idx}
                className="pt-sec-row"
                onClick={clickable ? () => renderRowClick(it) : undefined}
                style={clickable ? { cursor: "pointer" } : undefined}
              >
                <span className="l">
                  {it.description}
                  {clickable && (
                    <span style={{ color: "var(--pt-muted)", fontSize: 10, marginLeft: 6, textDecoration: "underline" }}>
                      view breakdown
                    </span>
                  )}

                </span>
                <span className="a" style={{ color: signClass }}>
                  {sign}{money2(it.amount)}
                </span>
              </div>
            );
          })}

          {editable && (
            <div className="pt-edit-add">
              <input
                className="desc"
                placeholder="Add description"
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doAdd()}
              />
              <input
                className="amt"
                placeholder="Amount"
                type="number"
                value={newAmt}
                onChange={(e) => setNewAmt(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doAdd()}
              />
              <button onClick={doAdd}>+ Add</button>
            </div>
          )}

          {children}

          <div className="pt-sec-total">
            <span style={{ color }}>{totalLabel}</span>
            <span style={{ color: signClass }}>{sign}{money2(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Month list
   ══════════════════════════════════════════════════════════ */

export function MonthsScreen({ nav, ctx, currentWeek }) {
  const { year, mode } = ctx;
  const now = new Date();

  return (
    <>
      <PTToolbar title={`Year ${year}`} onBack={() => nav("landing")} />
      <div className="pt-content">
        {MONTHS.map((m, idx) => {
          const month1 = idx + 1;
          const isFuture = year === now.getFullYear() && month1 > now.getMonth() + 1;
          const isCurrent =
            year === currentWeek.year && month1 === currentWeek.month;

          return (
            <button
              key={m}
              disabled={isFuture}
              className={`pt-listbtn ${isCurrent ? "current" : ""}`}
              onClick={() => !isFuture && nav("weeks", { year, month: m, mode })}
            >
              <div>
                <div className="name">{m}</div>
                <div className="meta">{isFuture ? "No data yet" : "Accounting Month"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isCurrent && <span className="pt-badge">Latest</span>}
                {!isFuture && <span style={{ color: "var(--pt-muted-2)", fontSize: 16 }}>›</span>}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Week list
   ══════════════════════════════════════════════════════════ */

export function WeeksScreen({ nav, ctx, currentWeek }) {
  const { year, month, mode } = ctx;
  const month1 = MONTHS.indexOf(month) + 1;
  const weeks = useMemo(() => weeksInMonth(year, month1), [year, month1]);

  return (
    <>
      <PTToolbar
        title={`${month} ${year}`}
        onBack={() => nav("months", { year, mode })}
      />
      <div className="pt-content">
        {weeks.length === 0 && (
          <div className="pt-empty">No weeks in this month.</div>
        )}
        {weeks.map((w) => {
          const isCurrent =
            year === currentWeek.year &&
            month1 === currentWeek.month &&
            currentWeek.week &&
            w.weekNumber === currentWeek.week.weekNumber;
          return (
            <button
              key={w.id}
              className={`pt-listbtn ${isCurrent ? "current" : ""}`}
              onClick={() => nav("detail", { year, month, week: w, mode })}
            >
              <div>
                <div className="name">
                  {w.id}{" "}
                  <span style={{ color: "var(--pt-muted)", fontWeight: 500, fontSize: 13 }}>
                    ({w.label})
                  </span>
                </div>
                <div className="meta">Accounting Week</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {isCurrent && <span className="pt-badge">Latest</span>}
                <span style={{ color: "var(--pt-muted-2)", fontSize: 16 }}>›</span>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   Weekly Detail — 8 tabs
   ══════════════════════════════════════════════════════════ */

const WEEKLY_TABS = [
  { id: "home", label: "Home" },
  { id: "locations", label: "Locations" },
  { id: "eric", label: "Eric" },
  { id: "liz", label: "Liz" },
  { id: "atm", label: "ATM" },
  { id: "delivery", label: "Delivery" },
  { id: "manager", label: "Manager" },
  { id: "summary", label: "Summary" },
];

export function WeeklyDetail({ nav, ctx, api, locations, isAdmin }) {
  const { year, month, week } = ctx;
  const month1 = MONTHS.indexOf(month) + 1;
  const [tab, setTab] = useState("home");
  const [weekData, setWeekData] = useState(null);
  const [summary, setSummary] = useState(null); // locationSummary response
  const [loading, setLoading] = useState(true); // locationSummary + saved week
  const [sharesReady, setSharesReady] = useState(false); // effective share layout resolved
  const [error, setError] = useState(null);
  // Investor layout effective for THIS week's start date: Map<locationName, investors[]>.
  // null until resolved / on error → callers fall back to the live layout.
  const [effectiveShares, setEffectiveShares] = useState(null);

  // edit-mode buckets (kept locally; saved in one POST)
  const [ericAdditions, setEricAdditions] = useState([]);
  const [ericSubtractions, setEricSubtractions] = useState([]);
  const [lizAdditions, setLizAdditions] = useState([]);
  const [lizSubtractions, setLizSubtractions] = useState([]);
  const [ericAtmUsed, setEricAtmUsed] = useState(0);
  const [lizCashInHand, setLizCashInHand] = useState(0);
  const [editing, setEditing] = useState({ eric: false, liz: false });
  const [saving, setSaving] = useState(false);
  const [saveNote, setSaveNote] = useState(null);
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Payment tracking (Eric tab). Unlike the NDS buckets these are NOT part of
  // the Edit/Save flow — each step is posted on its own by whichever partner
  // owns it, and `paymentRoles` comes from the server so the two authorised
  // accounts aren't duplicated client-side.
  const [payment, setPayment] = useState(EMPTY_PAYMENT);
  const [paymentRoles, setPaymentRoles] = useState(NO_PAYMENT_ROLES);
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  // Always fetch the summary for every location (archived included) so we can
  // tell which archived ones actually earned this week. Archived locations are
  // filtered out of the display below ONLY when they have no value this week —
  // so past weeks where they earned still show them, and their contribution to
  // any total is never dropped.
  const locationNames = useMemo(() => locations.map((l) => l.name), [locations]);
  // Which of those are archived, so the ATM views can apply the same rule.
  const archivedNames = useMemo(
    () => locations.filter((l) => l.archived).map((l) => l.name),
    [locations],
  );

  // ATM data hook lives here so Refresh + Save can be surfaced on the
  // Profit Tracker toolbar while the user is on the ATM sub-tab (same UX
  // as Eric/Liz). Still just one useATMData call per week — its effect
  // is keyed on week + locationNames.
  const atmData = useATMData(ctx, api, locationNames, archivedNames);
  const [atmRefreshConfirmOpen, setAtmRefreshConfirmOpen] = useState(false);

  // Lazy, cached per-location weekly report for the Manager & Summary chips.
  // Shared across both tabs so opening a location on one is instant on the other.
  const weekReport = useWeekReport(ctx, api);

  useEffect(() => {
    if (!week) return;
    setLoading(true);
    setError(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);

    let gotSummary = false;
    let gotWeek = false;
    const done = () => {
      if (gotSummary && gotWeek) setLoading(false);
    };

    api.fetchLocationSummary(
      { fromDate, toDate, locationNames },
      (res) => {
        setSummary(res?.sessions ? res : { sessions: res?.sessions || res || [] });
        gotSummary = true;
        done();
      },
      (err) => {
        setError(typeof err === "string" ? err : "Failed to load location summary");
        gotSummary = true;
        done();
      },
    );

    api.fetchWeek(
      { year, month: month1, weekNumber: week.weekNumber },
      (res) => {
        const w = res?.week || null;
        setWeekData(w);
        if (w) {
          const stamp = (arr, bucket) =>
            (arr || []).map((x) => {
              const base = {
                _id: x._id || x.id || newItemId(),
                description: x.description ?? x.desc ?? "",
                amount: Number(x.amount) || 0,
                locationName: x.locationName || "",
                isManual: x.isManual === true,
                _source: bucket,
              };
              base.category = x.category || inferCategory(base, bucket);
              return base;
            });
          setEricAdditions(stamp(w.ericAdditions, "ericAdd"));
          setEricSubtractions(stamp(w.ericSubtractions, "ericSub"));
          setLizAdditions(stamp(w.lizAdditions, "lizAdd"));
          setLizSubtractions(stamp(w.lizSubtractions, "lizSub"));
          setEricAtmUsed(Number(w.ericExtra?.atm) || 0);
          setLizCashInHand(Number(w.lizCashInHand) || 0);
        }
        setPayment(paymentFromWeek(w));
        setPaymentRoles(normalizePaymentRoles(res?.paymentRoles));
        setPaymentError(null);
        gotWeek = true;
        done();
      },
      (err) => {
        // no saved week is fine — treat as empty
        setWeekData(null);
        setPayment(EMPTY_PAYMENT);
        setPaymentRoles(NO_PAYMENT_ROLES);
        gotWeek = true;
        done();
      },
    );
  }, [year, month1, week, api, locationNames]);

  // Resolve the share layout in force on this week's start date. Historical
  // payouts must use the shares that were effective then, not the live (current)
  // investor records — editing a share would otherwise rewrite past reports.
  useEffect(() => {
    if (!week) return;
    setSharesReady(false);
    const { fromDate } = weekRange(year, month1, week.weekNumber);
    api.fetchEffectiveConfig(
      { date: toISO(fromDate) },
      (res) => {
        const map = new Map();
        (res?.locations || []).forEach((l) => map.set(l.name, l.investors || []));
        setEffectiveShares(map);
        setSharesReady(true);
      },
      () => {
        setEffectiveShares(null);
        setSharesReady(true);
      },
    );
  }, [year, month1, week, api]);

  // derive NDS items from locationSummary if we don't have a saved week yet.
  // This mirrors the backfill behaviour of the old LizAccounting page on first open.
  useEffect(() => {
    if (!summary || weekData) return;
    const {
      ericAdds, ericSubs, lizAdds, lizSubs,
      totalCashInHand, totalOwesAmount,
    } = deriveNdsBuckets(summary.sessions || []);
    setEricAdditions(ericAdds);
    setEricSubtractions(ericSubs);
    setLizAdditions(lizAdds);
    setLizSubtractions(lizSubs);
    // Eric's "Owes Me From ADV ATM" line = totalOwesAmount
    // (sum of ATM_ADV + ATM_DIDNT_USE) per LizAccounting.ericExtra.owes.
    setEricAtmUsed(totalOwesAmount);
    setLizCashInHand(totalCashInHand);
  }, [summary, weekData]);

  // locations with their netProfit for this week (derived from locationSummary)
  const liveLocations = useMemo(() => {
    const sessions = summary?.sessions || [];
    const byName = new Map();
    for (const s of sessions) {
      const ms = s.managerSummary || {};
      byName.set(s.locationName, {
        name: s.locationName,
        netProfit: Number(ms.weeklyNetProfit) || 0,
        delivery: ms.delivery || {},
        atm: (ms.atmTransactions || []).reduce(
          (acc, t) => {
            if (t.type === "ATM_CASH") acc.cash += Number(t.amount) || 0;
            else if (t.type === "ATM_ADV") acc.adv += Number(t.amount) || 0;
            else if (t.type === "ATM_FROM_PROFIT") acc.profit += Number(t.amount) || 0;
            else if (t.type === "ATM_DIDNT_USE") acc.didntUse += Number(t.amount) || 0;
            return acc;
          },
          { cash: 0, adv: 0, profit: 0, didntUse: 0 },
        ),
        manager: ms.managerName || "",
        // Set when Total Delivered ≠ Adjusted Total — the location cards paint
        // themselves red so an out-of-balance week is visible from the list.
        mismatch: deliveryMismatch(ms),
      });
    }
    return locations.map((l) => {
      const base = byName.get(l.name) || {
        netProfit: 0,
        delivery: {},
        atm: { cash: 0, adv: 0, profit: 0, didntUse: 0 },
        manager: "",
        mismatch: null,
      };
      // Use the share layout effective for this week; fall back to live investors.
      const eff = effectiveShares ? effectiveShares.get(l.name) : null;
      return { ...l, ...base, investors: eff || l.investors || [] };
    });
  }, [locations, summary, effectiveShares]);

  // What the tabs actually render: hide an archived location only when it has no
  // value this week. Active locations always show; any archived location that
  // earned/moved money this week still shows, so nothing real disappears and no
  // total changes (a hidden row contributed exactly 0).
  const visibleLocations = useMemo(
    () => liveLocations.filter((l) => !l.archived || locationHasWeekValue(l)),
    [liveLocations],
  );

  const handleSave = () => {
    setSaving(true);
    setSaveNote(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const stripInternal = (arr) =>
      arr.map(({ _id, _source, ...rest }) => rest);
    const payload = {
      year,
      month: month1,
      weekNumber: week.weekNumber,
      fromDate: toISO(fromDate),
      toDate: toISO(toDate),
      weeklyProfits: weekData?.weeklyProfits || [],
      unassignedAdditions: weekData?.unassignedAdditions || [],
      unassignedSubtractions: weekData?.unassignedSubtractions || [],
      unassignedOthers: weekData?.unassignedOthers || [],
      ericAdditions: stripInternal(ericAdditions),
      ericSubtractions: stripInternal(ericSubtractions),
      lizAdditions: stripInternal(lizAdditions),
      lizSubtractions: stripInternal(lizSubtractions),
      ericExtra: { atm: ericAtmUsed, owes: 0 },
      lizCashInHand,
    };
    api.saveWeek(
      payload,
      () => {
        setSaving(false);
        setSaveNote({ kind: "ok", text: "Saved" });
        setEditing({ eric: false, liz: false });
      },
      (err) => {
        setSaving(false);
        setSaveNote({ kind: "err", text: typeof err === "string" ? err : "Save failed" });
      },
    );
  };

  // Refresh — matches GM Profits' refresh flow (LizAccounting.applyDateRange +
  // restore manual entries). Re-pulls LOCATION_SUMMARY, rebuilds every NDS
  // bucket (Eric/Liz additions + subtractions), then appends any user-added
  // (isManual === true) rows the user had pending so their work isn't lost.
  const handleRefresh = () => {
    if (!week) return;
    setRefreshing(true);
    setSaveNote(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const keepManual = (arr) => (arr || []).filter((x) => x.isManual === true);
    const manualBackup = {
      ericAdd: keepManual(ericAdditions),
      ericSub: keepManual(ericSubtractions),
      lizAdd: keepManual(lizAdditions),
      lizSub: keepManual(lizSubtractions),
    };

    api.fetchLocationSummary(
      { fromDate, toDate, locationNames },
      (res) => {
        const sessions = res?.sessions || [];
        setSummary({ sessions });
        const {
          ericAdds, ericSubs, lizAdds, lizSubs,
          totalCashInHand, totalOwesAmount,
        } = deriveNdsBuckets(sessions);
        setEricAdditions([...ericAdds, ...manualBackup.ericAdd]);
        setEricSubtractions([...ericSubs, ...manualBackup.ericSub]);
        setLizAdditions([...lizAdds, ...manualBackup.lizAdd]);
        setLizSubtractions([...lizSubs, ...manualBackup.lizSub]);
        setEricAtmUsed(totalOwesAmount);
        setLizCashInHand(totalCashInHand);
        setRefreshing(false);
        setSaveNote({ kind: "ok", text: "Week data refreshed" });
        setTimeout(() => setSaveNote(null), 2500);
      },
      (err) => {
        setRefreshing(false);
        setSaveNote({ kind: "err", text: typeof err === "string" ? err : "Refresh failed" });
      },
    );
  };

  // Post one payment step. `kind` picks the endpoint; the server re-checks the
  // caller's account and returns the updated week so the panels, chips and
  // history all refresh from the saved record rather than optimistic state.
  const submitPayment = (kind, fields) => {
    if (!week) return;
    setPaymentBusy(true);
    setPaymentError(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const payload = {
      year,
      month: month1,
      weekNumber: week.weekNumber,
      fromDate: toISO(fromDate),
      toDate: toISO(toDate),
      ...fields,
    };
    const send =
      kind === "report"
        ? api.reportPayment
        : kind === "unverify"
        ? api.unverifyPayment
        : api.verifyPayment;
    send(
      payload,
      (res) => {
        setPaymentBusy(false);
        if (res?.success) {
          // Only the payment fields are taken from the response — `weekData`
          // deliberately stays as-is so reporting a payment never disturbs the
          // NDS buckets currently on screen.
          setPayment(paymentFromWeek(res.week));
          if (res.paymentRoles) setPaymentRoles(normalizePaymentRoles(res.paymentRoles));
        } else {
          setPaymentError(res?.message || "Could not save the payment.");
        }
      },
      (err) => {
        setPaymentBusy(false);
        setPaymentError(typeof err === "string" ? err : "Could not save the payment.");
      },
    );
  };

  const isPartnerTab = tab === "eric" || tab === "liz";
  const isPartnerEditing = tab === "eric" ? editing.eric : tab === "liz" ? editing.liz : false;
  const toolbarActions = [];
  // Refresh is admin-only and only surfaces on the Eric / Liz tabs (where
  // NDS re-pull actually matters).
  if (isAdmin && isPartnerTab) {
    toolbarActions.push({
      icon: <RefreshIcon />,
      title: refreshing ? "Refreshing…" : "Refresh",
      handler: () => setRefreshConfirmOpen(true),
      disabled: refreshing,
    });
  }
  // ATM tab gets its own Refresh + Save routed through the ATM hook, so the
  // user sees the same Refresh / Save contextual-menu pattern as Eric / Liz.
  if (isAdmin && tab === "atm") {
    toolbarActions.push({
      icon: <RefreshIcon />,
      title: atmData.refreshing ? "Refreshing…" : "Refresh",
      handler: () => setAtmRefreshConfirmOpen(true),
      disabled: atmData.refreshing || atmData.saving,
    });
    toolbarActions.push({
      icon: <SaveIcon />,
      title: atmData.saving ? "Saving…" : "Save",
      handler: atmData.saveWeek,
      disabled: atmData.saving || atmData.refreshing,
    });
  }
  // Delivery tab surfaces a "Download PDF" action in the contextual menu.
  if (tab === "delivery") {
    toolbarActions.push({
      icon: <GetAppIcon />,
      title: "Download PDF",
      handler: () => downloadDeliveryPdf({ locations: visibleLocations, week, year }),
    });
  }
  // Edit/Save toolbar actions only surface for admins. Non-admins see the
  // same tabs in view-only mode.
  if (isPartnerTab && isAdmin) {
    if (isPartnerEditing) {
      toolbarActions.push({
        icon: <SaveIcon />,
        title: saving ? "Saving…" : "Save",
        handler: handleSave,
        disabled: saving,
      });
    }
    toolbarActions.push({
      icon: <EditIcon />,
      title: isPartnerEditing ? "Done editing" : "Edit",
      handler: () => setEditing((e) => ({ ...e, [tab]: !e[tab] })),
    });
  }

  // Keep the spinner up until EVERY shared fetch is in: the location summary,
  // the saved week, and the effective share layout. The summary now drives a
  // live weekly-report recompute per location, so partial renders would flash
  // empty/stale numbers before the manager & summary data arrives.
  const loadingAll = loading || !sharesReady;

  return (
    <>
      <PTToolbar
        title={`${week.id} · ${week.label}, ${year}`}
        onBack={() => nav("weeks", { year, month, mode: "weekly" })}
        actions={toolbarActions}
      />
      <div className="pt-content">
      <div className="pt-subtabs">
        {WEEKLY_TABS.map((t) => (
          <button
            key={t.id}
            className={`pt-subtab ${tab === t.id ? "is-active" : ""}`}
            onClick={() => { setTab(t.id); scrollPtToTop(); }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="pt-error">{error}</div>}
      {loadingAll && <Spinner label="Loading week…" />}

      {!loadingAll && (
        <>
          {tab === "home" && (
            <HomeTab locations={visibleLocations} />
          )}
          {tab === "locations" && (
            <LocationsTab locations={visibleLocations} />
          )}
          {tab === "eric" && (
            <EricTab
              locations={visibleLocations}
              additions={ericAdditions}
              subtractions={ericSubtractions}
              lizSubtractions={lizSubtractions}
              setAdditions={setEricAdditions}
              setSubtractions={setEricSubtractions}
              atmCashUsed={ericAtmUsed}
              onAtmCashUsedChange={setEricAtmUsed}
              editing={editing.eric}
              saveNote={saveNote}
              payment={payment}
              paymentRoles={paymentRoles}
              paymentBusy={paymentBusy}
              paymentError={paymentError}
              onReportPayment={(fields) => submitPayment("report", fields)}
              onVerifyPayment={(fields) => submitPayment("verify", fields)}
              onUndoVerifyPayment={() => submitPayment("unverify", {})}
            />
          )}
          {tab === "liz" && (
            <LizTab
              locations={visibleLocations}
              additions={lizAdditions}
              subtractions={lizSubtractions}
              setAdditions={setLizAdditions}
              setSubtractions={setLizSubtractions}
              cashInHand={lizCashInHand}
              onCashInHandChange={setLizCashInHand}
              ericAdditions={ericAdditions}
              ericSubtractions={ericSubtractions}
              editing={editing.liz}
              saveNote={saveNote}
            />
          )}
          {tab === "atm" && (
            <ATMContent
              data={atmData}
              isAdmin={isAdmin}
              hideInlineActions
            />
          )}
          {tab === "delivery" && (
            <DeliveryTab locations={visibleLocations} />
          )}
          {tab === "manager" && (
            <ManagerTab locations={visibleLocations} weekReport={weekReport} />
          )}
          {tab === "summary" && (
            <SummaryTab locations={visibleLocations} weekReport={weekReport} />
          )}
        </>
      )}
      </div>

      <ConfirmDialog
        open={refreshConfirmOpen}
        title="Refresh Week Data?"
        message="This will re-pull the latest figures from the location summary and overwrite every NDS row on Eric and Liz. Your manual entries are preserved."
        confirmLabel={refreshing ? "Refreshing…" : "Refresh"}
        cancelLabel="Cancel"
        busy={refreshing}
        onClose={() => setRefreshConfirmOpen(false)}
        onConfirm={() => {
          setRefreshConfirmOpen(false);
          handleRefresh();
        }}
      />

      <ConfirmDialog
        open={atmRefreshConfirmOpen}
        title="Refresh ATM Records"
        message="This will re-pull every row from the Manager Summary. Any in-progress edits on auto rows will be overwritten; manual rows and mid-week advances are preserved."
        confirmLabel={atmData.refreshing ? "Refreshing…" : "Refresh"}
        cancelLabel="Cancel"
        busy={atmData.refreshing}
        onClose={() => setAtmRefreshConfirmOpen(false)}
        onConfirm={() => {
          setAtmRefreshConfirmOpen(false);
          atmData.refresh();
        }}
      />
    </>
  );
}

// Derive Eric/Liz NDS buckets plus aggregate side-values from a locationSummary response.
// Every item is stamped with a `category` so the per-partner UI can render sections
// (Positive, ATM Negative, ATM Advances, Zelles & Deductions for Liz; Positive,
// Deductions, ATM Advance for Eric) without re-inspecting the description each render.
function deriveNdsBuckets(sessions) {
  const ericAdds = [];
  const ericSubs = [];
  const lizAdds = [];
  const lizSubs = [];
  let totalCashInHand = 0;
  let totalAtmReceipts = 0;
  let totalOwesAmount = 0;

  const mk = (it) => ({
    _id: newItemId(),
    description: it.description,
    amount: Number(it.amount) || 0,
    locationName: it.locationName || "",
    isManual: false,
    category: it.category,
  });

  for (const s of sessions || []) {
    const ms = s.managerSummary || {};
    const loc = s.locationName;
    const managerName = (ms.managerName || "Unknown").trim();
    const delivery = ms.delivery || {};

    // Aggregate side-values used by the Eric/Liz final lines
    totalCashInHand += Number(delivery.cash) || 0;
    totalAtmReceipts += Number(delivery.atmReceipts) || 0;

    // Zelle -> Liz "Zelles & Deductions"
    if (Number(delivery.managerZelle) > 0) {
      lizSubs.push(mk({ description: "Zelle", amount: delivery.managerZelle, locationName: loc, category: "zelle" }));
    }
    // ATM ADV -> Liz "ATM Advances"
    if (Number(delivery.atmAdv) > 0) {
      lizSubs.push(mk({ description: "ATM ADV", amount: delivery.atmAdv, locationName: loc, category: "atmAdv" }));
    }

    // ATM transactions
    for (const t of ms.atmTransactions || []) {
      const amt = Number(t.amount) || 0;
      if (t.type === "ATM_ADV" || t.type === "ATM_DIDNT_USE") totalOwesAmount += amt;
      if (t.type === "ATM_DIDNT_USE" && amt > 0) {
        const desc = `ATM didn't use (${managerName})`;
        // Eric: goes in his Positive bucket. Liz: goes in her ATM Didn't Use section.
        ericAdds.push(mk({ description: desc, amount: amt, locationName: loc, category: "positive" }));
        lizSubs.push(mk({ description: desc, amount: amt, locationName: loc, category: "atmNeg" }));
      }
    }

    // Adjustments — route by assignedTo; additions = Positive, deductions default to "parts"
    // for Eric and "zelle" for Liz (they were historically small deductions; manual items
    // can be moved via edit).
    const adj = ms.adjustments || {};
    for (const a of adj.additions || []) {
      const tgt = (a.assignedTo || "").trim().toLowerCase();
      const item = mk({ description: a.description, amount: a.amount, locationName: loc, category: "positive" });
      if (tgt === "eric") ericAdds.push(item);
      else if (tgt === "liz") lizAdds.push(item);
    }
    for (const d of adj.deductions || []) {
      const tgt = (d.assignedTo || "").trim().toLowerCase();
      if (tgt === "eric") ericSubs.push(mk({ description: d.description, amount: d.amount, locationName: loc, category: "parts" }));
      else if (tgt === "liz") lizSubs.push(mk({ description: d.description, amount: d.amount, locationName: loc, category: "zelle" }));
    }
    for (const o of ms.otherItems || []) {
      const tgt = (o.assignedTo || "").trim().toLowerCase();
      if (tgt === "eric") ericSubs.push(mk({ description: o.description, amount: o.amount, locationName: loc, category: "parts" }));
      else if (tgt === "liz") lizSubs.push(mk({ description: o.description, amount: o.amount, locationName: loc, category: "zelle" }));
    }
  }

  // SAMM — standing $450 line.
  //   Liz Additions: +$450
  //   Eric Deductions: -$450 (mirror, so Eric tab shows the offset in his Deductions section)
  if (!lizAdds.some((x) => x.description === "SAMM")) {
    lizAdds.push(mk({ description: "SAMM", amount: 450, locationName: "", category: "positive" }));
  }
  if (!ericSubs.some((x) => x.description === "SAMM")) {
    ericSubs.push(mk({ description: "SAMM", amount: 450, locationName: "", category: "parts" }));
  }

  // ATM Used from Profit — Eric ATM Advance.
  // Per GM Profits: ericExtra.atm === sum of delivery.atmReceipts; the final
  // computation deducts it BEFORE totalling, so we render it as a standing NDS
  // line in Eric's ATM Advance section.
  if (totalAtmReceipts > 0 && !ericSubs.some((x) => x.description === "ATM Used from Profit")) {
    ericSubs.push(mk({ description: "ATM Used from Profit", amount: totalAtmReceipts, locationName: "", category: "atmAdv" }));
  }

  return {
    ericAdds, ericSubs, lizAdds, lizSubs,
    totalCashInHand, totalAtmReceipts, totalOwesAmount,
  };
}

// When a saved week is loaded, items may not have `category` set (older data).
// Infer one from the description so the new UI can still slot them into the right section.
function inferCategory(item, bucket /* "ericAdd" | "ericSub" | "lizAdd" | "lizSub" */) {
  const d = (item.description || "").trim().toLowerCase();
  if (bucket === "ericAdd" || bucket === "lizAdd") return "positive";
  if (bucket === "ericSub") {
    if (d === "samm") return "parts";
    if (d.startsWith("atm used") || d.startsWith("atm adv") || d.includes("atm didn't use")) return "atmAdv";
    return "parts";
  }
  // lizSub
  if (d.includes("atm didn't use")) return "atmNeg";
  if (d === "zelle") return "zelle";
  if (d.startsWith("atm adv")) return "atmAdv";
  return "zelle";
}

/* ─── HomeTab ───────────────────────────────────────────────── */

function HomeTab({ locations }) {
  const totalProfit = sum(locations, (l) => l.netProfit);
  const active = locations.filter((l) => (Number(l.netProfit) || 0) > 0).length;
  const atmTotal = sum(locations, (l) => l.atm?.cash || 0);

  const partnerTotal = (partnerName) => {
    let total = 0;
    for (const l of locations) {
      for (const inv of l.investors || []) {
        if (inv.name === partnerName) total += computeShare(l, inv);
      }
    }
    return total;
  };
  const liz = partnerTotal("Liz");
  const eric = partnerTotal("Eric");

  const top5 = [...locations]
    .filter((l) => (Number(l.netProfit) || 0) > 0)
    .sort((a, b) => (Number(b.netProfit) || 0) - (Number(a.netProfit) || 0))
    .slice(0, 5);
  const max = top5[0]?.netProfit || 1;

  return (
    <>
      <div className="pt-hero">
        <div className="lb">Total Net Profit</div>
        <div className="big">{money2(totalProfit)}</div>
        <div className="stats">
          <div className="stat">
            <div>
              <div className="k">Active</div>
              <div className="v">
                {active}<span style={{ color: "rgba(255,255,255,0.5)", fontSize: 12 }}>/{locations.length}</span>
              </div>
            </div>
          </div>
          <div className="sep" />
          <div className="stat">
            <div>
              <div className="k">ATM Total</div>
              <div className="v">{money0(atmTotal)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="pt-split">
        <div className="card liz">
          <div className="nm">Liz</div>
          <div className="val">{money2(liz)}</div>
          <div className="sub">Base earnings</div>
        </div>
        <div className="card eric">
          <div className="nm">Eric</div>
          <div className="val">{money2(eric)}</div>
          <div className="sub">Base earnings</div>
        </div>
      </div>

      <div className="pt-card">
        <div className="pt-lb"><span role="img" aria-label="trophy">🏆</span> Top 5 Locations</div>
        {top5.length === 0 && <div style={{ color: "var(--pt-muted-2)", fontSize: 12 }}>No profitable locations this week.</div>}
        {top5.map((l, i) => (
          <div key={l.name} style={{ marginBottom: i < top5.length - 1 ? 10 : 0 }}>
            <div className="pt-toprow">
              <span className="nm">{i + 1}. {l.name}</span>
              <span className="v">{money2(l.netProfit)}</span>
            </div>
            <div className="pt-bar">
              <span style={{ width: `${(l.netProfit / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

/* ─── LocationsTab ──────────────────────────────────────────── */

function LocationsTab({ locations }) {
  const [expanded, setExpanded] = useState(null);
  const sorted = [...locations].sort(
    (a, b) => (Number(b.netProfit) || 0) - (Number(a.netProfit) || 0),
  );

  return (
    <>
      <div className="pt-lb" style={{marginTop:'8px'}}>All Locations</div>
      {sorted.length === 0 && (
        <div className="pt-empty">No locations to display.</div>
      )}
      {sorted.map((loc) => {
        const isOpen = expanded === loc.locationId;
        const hasProfit = (Number(loc.netProfit) || 0) > 0;
        const code = (loc.name || "").replace(/[^A-Z0-9]/gi, "").slice(0, 3).toUpperCase();
        return (
          <div
            key={loc.locationId}
            className={`pt-loc ${loc.mismatch ? "is-mismatch" : ""}`}
            title={mismatchTitle(loc.mismatch)}
          >
            <button
              className="pt-loc-head"
              onClick={() => setExpanded(isOpen ? null : loc.locationId)}
            >
              <div className={`pt-loc-badge ${hasProfit ? "active" : ""}`}>{code}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{loc.name}</div>
                <div style={{ color: "var(--pt-muted-2)", fontSize: 11, marginTop: 1 }}>
                  Profit:{" "}
                  <span style={{ color: hasProfit ? "var(--pt-green)" : "var(--pt-muted-2)", fontWeight: 700 }}>
                    {money2(loc.netProfit || 0)}
                  </span>
                  {(loc.atm?.cash || 0) > 0 && (
                    <span style={{ color: "var(--pt-orange)", marginLeft: 8 }}>
                      ATM {money0(loc.atm.cash)}
                    </span>
                  )}
                </div>
              </div>
              <span style={{ color: "var(--pt-muted-2)", fontSize: 13, transform: isOpen ? "rotate(180deg)" : "rotate(0)" }}>
                ▼
              </span>
            </button>
            {isOpen && (
              <div className="pt-loc-body">
                {(loc.investors || []).filter((i) => i.isManager).map((m) => (
                  <div key={m.investorId} className="pt-mgr">
                    <span>Manager <b>{m.name}</b> ({m.percentage}%)</span>
                    <b>{money2(computeShare(loc, m))}</b>
                  </div>
                ))}
                <div className="pt-pvsplit">
                  {(loc.investors || []).filter((i) => !i.isManager).map((inv) => {
                    const variant = inv.name === "Liz" ? "liz" : inv.name === "Eric" ? "eric" : "other";
                    return (
                      <div key={inv.investorId} className={`card ${variant}`}>
                        <div className="lb">{inv.name}</div>
                        <div className="val">{money2(computeShare(loc, inv))}</div>
                        <div className="pct">{inv.percentage}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/* ─── Shared partner helpers ───────────────────────────────── */

// Classify a description as one of the collapse buckets used by the Positive
// CombinableSectionCard.
//   donations  -> any description containing "donation" / "donations"
//   sams       -> any description containing the standalone word "sam", "sams" or "sam's"
//   atmDidntUse -> any description containing "atm didn't use" (with or without apostrophe)
//   system     -> any description containing the standalone word "system"
// SAMM (the $450 monthly line) is explicitly excluded because its extra 'm' breaks the word boundary.
function collapseBucket(desc) {
  const s = (desc || "").toLowerCase().trim();
  if (/\bdonations?\b/.test(s)) return "donations";
  if (/\bsam(?:'s|s)?\b/.test(s)) return "sams";
  if (/atm\s*didn'?t\s*use/.test(s)) return "atmDidntUse";
  if (/\bsystem\b/.test(s)) return "system";
  return null;
}

function EarningsByLocation({ partnerName, accent, locations }) {
  const earnings = useMemo(() => {
    return locations
      .map((l) => {
        const inv = (l.investors || []).find((i) => i.name === partnerName);
        if (!inv) return null;
        return {
          description: `${l.name} (${inv.percentage}%)`,
          amount: computeShare(l, inv),
          isManual: false,
          locationName: l.name,
          _id: `earn_${l.locationId}`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.amount - a.amount)
      .map((item, idx) => ({
        ...item,
        description: `${idx + 1}. ${item.description}`,
      }));
  }, [locations, partnerName]);

  return {
    node: (
      <SectionCard
        title="📍 Earnings by Location"
        color={accent}
        items={earnings}
        sign="+"
      />
    ),
    baseEarnings: sum(earnings, (x) => x.amount),
  };
}

/* ═══════════════════════════════════════════════════════════
   Payment tracking — two-step settlement confirmation
   ══════════════════════════════════════════════════════════ */

// Shape the Eric tab consumes. Kept out of `weekData` because each step is
// posted on its own endpoint, outside the week's Edit/Save flow.
const EMPTY_PAYMENT = { report: null, verification: null, history: [] };
const NO_PAYMENT_ROLES = {
  canReport: false,
  canVerify: false,
  payerUsername: "",
  verifierUsername: "",
  payerName: "",
  verifierName: "",
};

// Mongo stores an unset Map as `{}`, so "present" means "has keys".
const hasFields = (o) => !!o && typeof o === "object" && Object.keys(o).length > 0;

function paymentFromWeek(w) {
  return {
    report: hasFields(w?.paymentReport) ? w.paymentReport : null,
    verification: hasFields(w?.paymentVerification) ? w.paymentVerification : null,
    history: Array.isArray(w?.paymentHistory) ? w.paymentHistory : [],
  };
}

// Who may do what comes from the server (getWeek / reportPayment responses) so
// the two authorised accounts live in exactly one place — the backend config.
function normalizePaymentRoles(roles) {
  return {
    canReport: roles?.canReport === true,
    canVerify: roles?.canVerify === true,
    payerUsername: roles?.payerUsername || "",
    verifierUsername: roles?.verifierUsername || "",
    // Short labels for the panels — the account is what the server enforces,
    // but quoting an email at the reader is noise. Falls back to the username
    // so a server that predates these fields still says something.
    payerName: roles?.payerName || roles?.payerUsername || "",
    verifierName: roles?.verifierName || roles?.verifierUsername || "",
  };
}

// "2026-09-14" → "Sep 14, 2026". Split by hand rather than `new Date(str)`,
// which parses a bare ISO date as UTC midnight and renders as the day before
// in every timezone west of Greenwich.
function fmtPayDate(value) {
  if (!value) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!m) return String(value);
  return `${MONTHS_SHORT[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}


const todayISO = () => toISO(new Date());

/**
 * A person's name, minus the literal "null" that registration bakes in.
 * RegisterService/TeamService build displayName by interpolation
 * ("$firstname $lastname"), so a user with no last name is stored as
 * "Eric null". The server now strips that on write, but records written
 * before that fix still carry it — clean it here so old rows read correctly
 * too, without a data migration.
 */
function cleanName(value) {
  return String(value == null ? "" : value)
    .split(/\s+/)
    .filter((w) => w && w.toLowerCase() !== "null" && w.toLowerCase() !== "undefined")
    .join(" ");
}

function PaymentStatusChip({ status }) {
  if (status === "verified") {
    return (
      <span className="pt-pay-chip ok">
        <CheckCircleIcon style={{ fontSize: 14 }} /> Verified
      </span>
    );
  }
  if (status === "pending") {
    // Two labels, one shown at a time by CSS. In a history row on a phone the
    // long label squeezes the entry title onto a second line, so that context
    // swaps to "Pending"; everywhere else keeps the full wording.
    return (
      <span className="pt-pay-chip wait">
        <ScheduleIcon style={{ fontSize: 14 }} />
        <span className="full">Waiting for verification</span>
        <span className="short">Pending</span>
      </span>
    );
  }
  return <span className="pt-pay-chip none">Not reported</span>;
}

function PaymentRow({ label, children }) {
  return (
    <div className="pt-pay-row">
      <span className="k">{label}</span>
      <span className="v">{children}</span>
    </div>
  );
}

/**
 * Full-size view of one stored receipt. Data URIs can't be opened as a
 * top-level navigation in Chrome, so the image has to be shown in-page —
 * which also means the browser's own pinch-zoom doesn't apply to it. The
 * scroll container below supplies that instead: the image is sized to the
 * current zoom factor and overflows, so a phone pans it by dragging and a
 * desktop by scrolling, with buttons and double-tap to change zoom.
 */
const RECEIPT_ZOOMS = [1, 1.75, 3];

function ReceiptLightbox({ receipt, onClose }) {
  const [zoomIdx, setZoomIdx] = useState(0);
  const paneRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Every newly opened receipt starts fitted, not at the previous zoom.
  useEffect(() => { setZoomIdx(0); }, [receipt]);

  // Re-centre after each zoom change. This has to run in an effect rather than
  // inside the click handler: the new image width isn't laid out until React
  // has committed, so measuring any earlier reads the old scrollWidth and the
  // view stays pinned to the top-left corner.
  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;
    pane.scrollLeft = (pane.scrollWidth - pane.clientWidth) / 2;
    pane.scrollTop = (pane.scrollHeight - pane.clientHeight) / 2;
  }, [zoomIdx, receipt]);

  if (!receipt) return null;
  const zoom = RECEIPT_ZOOMS[zoomIdx];
  const applyZoom = (nextIdx) => setZoomIdx(nextIdx);
  const cycleZoom = () => applyZoom((zoomIdx + 1) % RECEIPT_ZOOMS.length);

  return (
    <div className="pt-receipt-lightbox" role="presentation">
      {/* Backdrop closes; the pane itself must not, or a pan would dismiss it. */}
      <div className="backdrop" onClick={onClose} role="presentation" />
      <div className="pane" ref={paneRef}>
        <img
          src={receipt.dataUrl}
          alt={receipt.name || "Receipt"}
          className={zoom > 1 ? "zoomed" : ""}
          style={{ width: `${zoom * 100}%` }}
          onDoubleClick={cycleZoom}
          role="presentation"
        />
      </div>
      <div className="tools">
        <button
          type="button"
          onClick={() => applyZoom(Math.max(0, zoomIdx - 1))}
          disabled={zoomIdx === 0}
          aria-label="Zoom out"
        >
          −
        </button>
        <span className="lvl">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          onClick={() => applyZoom(Math.min(RECEIPT_ZOOMS.length - 1, zoomIdx + 1))}
          disabled={zoomIdx === RECEIPT_ZOOMS.length - 1}
          aria-label="Zoom in"
        >
          +
        </button>
        <button type="button" className="close" onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// Read-only strip of attached receipts, used on a saved report/verification.
function ReceiptStrip({ receipts, onOpen }) {
  if (!receipts || !receipts.length) return null;
  return (
    <div className="pt-receipt-strip">
      {receipts.map((r, i) => (
        <button
          type="button"
          className="pt-receipt-thumb"
          key={r.id || i}
          onClick={() => onOpen(r)}
          title={r.name || `Receipt ${i + 1}`}
        >
          <img src={r.dataUrl} alt={r.name || `Receipt ${i + 1}`} />
        </button>
      ))}
    </div>
  );
}

/**
 * Receipt picker. On a phone or tablet it offers both "Take Photo" (opens the
 * camera directly via `capture`) and "Choose File" (gallery / files). On a
 * computer only the upload button renders — there's no camera worth opening.
 */
function ReceiptPicker({ receipts, onChange, disabled }) {
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [viewing, setViewing] = useState(null);
  // Evaluated once — a device doesn't grow a touchscreen mid-session, and
  // re-checking per render would thrash the media query.
  const handheld = useMemo(() => isHandheld(), []);

  const full = receipts.length >= MAX_RECEIPTS;

  const handleFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    // Let the same file be re-picked after a remove.
    e.target.value = "";
    if (!picked.length) return;

    const room = MAX_RECEIPTS - receipts.length;
    const files = picked.slice(0, room);
    setError(picked.length > room ? `Only ${MAX_RECEIPTS} receipts can be attached.` : "");
    setBusy(true);
    const added = [];
    for (const file of files) {
      if (!/^image\//.test(file.type)) {
        setError("That file is not an image.");
        continue;
      }
      try {
        const out = await compressReceipt(file);
        if (!out || !out.dataUrl) throw new Error("Could not process that image");
        if (out.bytes > RECEIPT_MAX_BYTES * 2) {
          setError("That image is too large even after compression — try a smaller one.");
          continue;
        }
        added.push({
          id: newReceiptId(),
          dataUrl: out.dataUrl,
          name: file.name || "receipt.jpg",
          width: out.width,
          height: out.height,
          bytes: out.bytes,
        });
      } catch (err) {
        setError(err.message || "Could not read that image");
      }
    }
    setBusy(false);
    if (added.length) onChange([...receipts, ...added]);
  };

  return (
    <div className="pt-receipt-picker">
      <span className="k">Receipt</span>

      {receipts.length > 0 && (
        <div className="pt-receipt-strip">
          {receipts.map((r, i) => (
            <div className="pt-receipt-thumb editable" key={r.id || i}>
              <img
                src={r.dataUrl}
                alt={r.name || `Receipt ${i + 1}`}
                onClick={() => setViewing(r)}
                role="presentation"
              />
              <button
                type="button"
                className="rm"
                title="Remove"
                disabled={disabled || busy}
                onClick={() => onChange(receipts.filter((x, j) => (x.id || j) !== (r.id || i)))}
              >
                ×
              </button>
              <span className="sz">{Math.round((r.bytes || 0) / 1024)} KB</span>
            </div>
          ))}
        </div>
      )}

      {!full && (
        <div className="pt-receipt-buttons">
          {handheld && (
            <button
              type="button"
              className="pt-receipt-btn"
              disabled={disabled || busy}
              onClick={() => cameraRef.current && cameraRef.current.click()}
            >
              <PhotoCameraIcon style={{ fontSize: 18 }} />
              {busy ? "Processing…" : "Take Photo"}
            </button>
          )}
          <button
            type="button"
            className="pt-receipt-btn"
            disabled={disabled || busy}
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            <PublishIcon style={{ fontSize: 18 }} />
            {busy ? "Processing…" : handheld ? "Choose File" : "Upload Receipt"}
          </button>
        </div>
      )}

      {/* `capture` is what makes a phone open the camera instead of a picker;
          the second input deliberately omits it so the gallery/files chooser
          stays available. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFiles}
        style={{ display: "none" }}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFiles}
        style={{ display: "none" }}
      />

      {/* Only surface the cap once it's actually hit — the compression is an
          implementation detail nobody needs narrated. */}
      {full && (
        <div className="pt-receipt-hint">Maximum of {MAX_RECEIPTS} receipts attached.</div>
      )}
      {error && <div className="pt-receipt-error">{error}</div>}

      <ReceiptLightbox receipt={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

// Shared by both steps — same fields, different labels and endpoint. The
// receipt is the substance of a report (the amount is legible on it, so it
// isn't keyed in separately); the date defaults to today.
function PaymentForm({
  dateLabel, submitLabel, busy, accent, requireReceipt,
  initialDate, initialNote, initialReceipts,
  onSubmit, onCancel,
}) {
  const [date, setDate] = useState(initialDate || todayISO());
  const [note, setNote] = useState(initialNote || "");
  const [receipts, setReceipts] = useState(initialReceipts || []);

  const missingReceipt = requireReceipt && receipts.length === 0;

  return (
    <div className="pt-pay-form">
      <ReceiptPicker receipts={receipts} onChange={setReceipts} disabled={busy} />
      {/* Same calendar popup the Loans forms use — it takes and emits the
          plain 'YYYY-MM-DD' string this form already stores. */}
      <div className="pt-pay-field">
        <span className="k">{dateLabel}</span>
        <InlineDatePicker value={date} onChange={setDate} placeholder="Select a date" />
      </div>
      <label className="pt-pay-field">
        <span className="k">Note</span>
        <input
          type="text"
          placeholder="e.g. Left in the office"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      {/* A tooltip on the disabled button would be invisible on a phone, which
          is where most receipts get attached — say it in the layout instead. */}
      {missingReceipt && (
        <div className="pt-receipt-error">Attach the receipt before reporting the payment.</div>
      )}
      <div className="pt-pay-actions">
        {onCancel && (
          <button type="button" className="pt-pay-btn ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
        <button
          type="button"
          className="pt-pay-btn"
          style={{ background: accent }}
          disabled={busy || !date || missingReceipt}
          onClick={() => onSubmit({ date, note, receipts })}
        >
          {busy ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}

function PaymentHistory({ history }) {
  if (!history.length) return null;
  return (
    <div className="pt-pay-history">
      <div className="pt-pay-history-hd">Payment history</div>
      {history.map((h, i) => {
        // A reversal stays in the log next to the confirmation it undid — the
        // record should show what happened, not quietly lose a step.
        const kind =
          h.type === "verified" ? "ok" : h.type === "unverified" ? "undo" : "rep";
        const last = i === history.length - 1;
        return (
          // Four grid tracks: connector rail, icon badge, text, meta. On a
          // narrow screen the meta drops to its own row under the text while
          // the rail keeps spanning both — see .pt-pay-hist in the stylesheet.
          <div className="pt-pay-hist" key={`${h.at || "h"}_${i}`}>
            <span className="rail">
              <span className={`dot ${kind}`} />
              {!last && <span className="line" />}
            </span>
            <span className={`badge ${kind}`}>
              {kind === "ok" ? (
                <CheckCircleIcon style={{ fontSize: 32 }} />
              ) : kind === "undo" ? (
                <UndoIcon style={{ fontSize: 18 }} />
              ) : (
                <PublishIcon style={{ fontSize: 18 }} />
              )}
            </span>
            <div className="body">
              <div className="t">
                {cleanName(h.actor) || "—"}{" "}
                {kind === "ok"
                  ? "verified receipt"
                  : kind === "undo"
                  ? "undid verification"
                  : "reported payment"}
              </div>
              {h.note ? <div className="n">“{h.note}”</div> : null}
            </div>
            <div className="meta">
              <span className="d">{fmtPayDate(h.date || h.at)}</span>
              <PaymentStatusChip status={kind === "ok" ? "verified" : "pending"} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PaymentTracking({ payment, roles, busy, error, onReport, onVerify, onUndoVerify }) {
  const report = payment?.report || null;
  const verification = payment?.verification || null;
  const history = payment?.history || [];
  const can = roles || NO_PAYMENT_ROLES;

  const status = verification ? "verified" : report ? "pending" : "none";
  const [reportOpen, setReportOpen] = useState(false);
  const [viewing, setViewing] = useState(null); // saved receipt opened full-size
  const [undoOpen, setUndoOpen] = useState(false);

  const reportReceipts = useMemo(
    () => (Array.isArray(report?.receipts) ? report.receipts : []),
    [report],
  );
  const verifyReceipts = Array.isArray(verification?.receipts) ? verification.receipts : [];

  // A submitted report replaces the form with the saved rows.
  useEffect(() => { setReportOpen(false); }, [report, verification]);

  const showReportForm = can.canReport && !verification && (reportOpen || !report);
  const showVerifyForm = can.canVerify && !!report && !verification;

  return (
    <div className="pt-pay">
      <div className="pt-pay-hd">
        <CreditCardIcon style={{ fontSize: 26 }} />
        <div>
          <div className="t">Payment Tracking</div>
          <div className="s">
            Two-step payment confirmation between {can.payerName || "Eric"} and{" "}
            {can.verifierName || "Liz"}.
          </div>
        </div>
      </div>

      {error && <div className="pt-error" style={{ margin: "0 0 12px" }}>{error}</div>}

      <div className="pt-pay-grid">
        {/* ── Step 1: report (payer only) ───────────────────────── */}
        <div className="pt-pay-panel">
          <div className="pt-pay-panel-hd blue">
            <PublishIcon style={{ fontSize: 24 }} />
            <div>
              <div className="t">Report Payment</div>
              <div className="s">Used by the person who paid</div>
            </div>
          </div>
          <div className="pt-pay-panel-bd">
            {showReportForm ? (
              <PaymentForm
                dateLabel="Date paid"
                submitLabel={report ? "Update Report" : "Report Payment"}
                busy={busy}
                accent="var(--pt-blue)"
                initialDate={report?.datePaid || todayISO()}
                initialNote={report?.note || ""}
                initialReceipts={reportReceipts}
                requireReceipt
                onCancel={report ? () => setReportOpen(false) : null}
                onSubmit={({ date, note, receipts }) =>
                  onReport({ datePaid: date, note, receipts })
                }
              />
            ) : report ? (
              <>
                <PaymentRow label="Paid by:">{cleanName(report.paidBy) || "—"}</PaymentRow>
                <PaymentRow label="Date paid:">{fmtPayDate(report.datePaid)}</PaymentRow>
                <PaymentRow label="Note:">{report.note || "—"}</PaymentRow>
                <PaymentRow label="Status:">
                  <PaymentStatusChip status={status} />
                </PaymentRow>
                {reportReceipts.length > 0 && (
                  <>
                    <div className="pt-receipt-label">
                      Receipt{reportReceipts.length === 1 ? "" : "s"} — tap to enlarge
                    </div>
                    <ReceiptStrip receipts={reportReceipts} onOpen={setViewing} />
                  </>
                )}
                {can.canReport && !verification && (
                  <button
                    type="button"
                    className="pt-pay-btn ghost"
                    style={{ marginTop: 10 }}
                    onClick={() => setReportOpen(true)}
                  >
                    Update report
                  </button>
                )}
              </>
            ) : (
              <div className="pt-pay-empty">
                No payment reported yet.
                {can.payerName ? (
                  <div className="who">Only {can.payerName} can report a payment.</div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* ── Step 2: verify (receiver only) ────────────────────── */}
        <div className="pt-pay-panel">
          <div className="pt-pay-panel-hd green">
            <CheckCircleIcon style={{ fontSize: 24 }} />
            <div>
              <div className="t">Verify Receipt</div>
              <div className="s">Used by the person who received the money</div>
            </div>
          </div>
          <div className="pt-pay-panel-bd">
            {showVerifyForm ? (
              <>
                {/* The receipt being verified, read-only — the whole point of
                    this step is checking it against what arrived. */}
                {reportReceipts.length > 0 && (
                  <>
                    <div className="pt-receipt-label">
                      Reported receipt{reportReceipts.length === 1 ? "" : "s"} — tap to enlarge
                    </div>
                    <ReceiptStrip receipts={reportReceipts} onOpen={setViewing} />
                  </>
                )}
                <PaymentForm
                  dateLabel="Date received"
                  submitLabel="Verify Receipt"
                  busy={busy}
                  accent="var(--pt-green)"
                  initialDate={todayISO()}
                  initialNote=""
                  initialReceipts={[]}
                  onSubmit={({ date, note, receipts }) =>
                    onVerify({ dateReceived: date, note, receipts })
                  }
                />
              </>
            ) : verification ? (
              <>
                <PaymentRow label="Received by:">{cleanName(verification.receivedBy) || "—"}</PaymentRow>
                <PaymentRow label="Date received:">
                  {fmtPayDate(verification.dateReceived)}
                </PaymentRow>
                <PaymentRow label="Note:">{verification.note || "—"}</PaymentRow>
                <PaymentRow label="Status:">
                  <PaymentStatusChip status="verified" />
                </PaymentRow>
                {verifyReceipts.length > 0 && (
                  <>
                    <div className="pt-receipt-label">
                      Receipt{verifyReceipts.length === 1 ? "" : "s"} — tap to enlarge
                    </div>
                    <ReceiptStrip receipts={verifyReceipts} onOpen={setViewing} />
                  </>
                )}
                {/* Escape hatch for a verification made by mistake. Only the
                    verifier sees it — the payer must not be able to unpick
                    someone else's confirmation. */}
                {can.canVerify && (
                  <button
                    type="button"
                    className="pt-pay-btn ghost"
                    style={{ marginTop: 12 }}
                    disabled={busy}
                    onClick={() => setUndoOpen(true)}
                  >
                    <UndoIcon style={{ fontSize: 16, marginRight: 6 }} />
                    Undo verification
                  </button>
                )}
              </>
            ) : (
              <div className="pt-pay-empty">
                {report
                  ? "Waiting for the receipt to be verified."
                  : "Nothing to verify — no payment has been reported yet."}
                {can.verifierName ? (
                  <div className="who">Only {can.verifierName} can verify a receipt.</div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <PaymentHistory history={history} />

      <ReceiptLightbox receipt={viewing} onClose={() => setViewing(null)} />

      <ConfirmDialog
        open={undoOpen}
        title="Undo this verification?"
        message={
          "The payment goes back to waiting for verification, so it can be confirmed again " +
          "and the reported receipt can be corrected. Both the original confirmation and " +
          "this reversal stay in the payment history."
        }
        confirmLabel={busy ? "Undoing…" : "Undo verification"}
        cancelLabel="Cancel"
        variant="danger"
        busy={busy}
        onClose={() => setUndoOpen(false)}
        onConfirm={() => {
          setUndoOpen(false);
          onUndoVerify();
        }}
      />
    </div>
  );
}

/* ─── Eric tab ─────────────────────────────────────────────── */

function EricTab({
  locations, additions, subtractions, lizSubtractions = [],
  setAdditions, setSubtractions,
  atmCashUsed, onAtmCashUsedChange,
  editing, saveNote,
  payment, paymentRoles, paymentBusy, paymentError,
  onReportPayment, onVerifyPayment, onUndoVerifyPayment,
}) {
  const accent = "var(--pt-green)";
  const bg = "var(--pt-eric-bg)";
  const [breakdown, setBreakdown] = useState(null); // { title, items, sign }

  const earnings = EarningsByLocation({ partnerName: "Eric", accent, locations });
  const baseEarnings = earnings.baseEarnings;

  // additions is a single list; everything routes to Positive for Eric.
  // subtractions carries category = "parts" | "atmAdv".
  const additionsDisplay = useMemo(
    () =>
      additions.map((it) =>
        it.isManual || !it.locationName
          ? it
          : { ...it, description: `${it.description} (${it.locationName})` }
      ),
    [additions]
  );
  const partsItems = subtractions.filter((x) => (x.category || "parts") === "parts");
  const atmAdvItems = subtractions.filter((x) => x.category === "atmAdv");

  const addTotal = sum(additions, (x) => x.amount);
  const partsTotal = sum(partsItems, (x) => x.amount);
  const atmAdvTotal = sum(atmAdvItems, (x) => x.amount);
  const atmCashUsedNum = Number(atmCashUsed) || 0;
  const atmCombined = atmAdvTotal + atmCashUsedNum;

  // The ATM breakdown modal (opened below) reads the real per-location
  // ATM ADV / ATM DIDN'T USE rows from the saved `lizSubtractions`
  // (categories "atmAdv" / "atmNeg"), whose sum equals atmCashUsedNum.
  const totalBeforeDeductions = baseEarnings + addTotal;
  const totalDeductions = partsTotal + atmCombined;
  const lizOwesEric = totalBeforeDeductions - totalDeductions;
  const isNeg = lizOwesEric < 0;
  const finalAbs = Math.abs(lizOwesEric);

  // ----- edit callbacks keyed by full-array index ------------------------
  const addToBucket = (setter, category) => (it) =>
    setter((arr) => [...arr, { ...it, category, _id: newItemId() }]);
  const removeFrom = (setter, filter) => (idxWithinFilter) =>
    setter((arr) => {
      const targets = arr
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => filter(it));
      const target = targets[idxWithinFilter];
      return target ? arr.filter((_, i) => i !== target.i) : arr;
    });
  const editIn = (setter, filter) => (idxWithinFilter, patch) =>
    setter((arr) => {
      const targets = arr
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => filter(it));
      const target = targets[idxWithinFilter];
      if (!target) return arr;
      return arr.map((it, i) => (i === target.i ? { ...it, ...patch } : it));
    });

  return (
    <>
      <div className="pt-lb" style={{ color: accent, marginBottom: 10, marginTop:8 }}>
        Eric's Weekly Summary {editing && <span style={{ color: "var(--pt-teal-txt)", marginLeft: 6 }}>— editing</span>}
      </div>
      {saveNote && (
        <div className={saveNote.kind === "ok" ? "pt-card" : "pt-error"} style={{ padding: "8px 12px", marginBottom: 10, color: saveNote.kind === "ok" ? "var(--pt-green)" : undefined }}>
          {saveNote.text}
        </div>
      )}

      {earnings.node}

      <div className="pt-card" style={{ background: bg }}>
        <div className="pt-lb">Total Base Earnings</div>
        <div className="pt-bg-num" style={{ color: accent }}>{money2(baseEarnings)}</div>
      </div>

      <CombinableSectionCard
        title="➕ Positive"
        color="var(--pt-green)"
        items={additionsDisplay}
        sign="+"
        editable={editing}
        onAdd={addToBucket(setAdditions, "positive")}
        onRemove={(idx) => setAdditions((a) => a.filter((_, i) => i !== idx))}
        onEditItem={(idx, patch) => setAdditions((a) => a.map((x, i) => (i === idx ? { ...x, ...patch } : x)))}
        onOpenBreakdown={setBreakdown}
      />

      <div className="pt-card" style={{ background: bg }}>
        <div className="pt-lb">Total Before Deductions (A)</div>
        <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 2, marginBottom: 4 }}>
          Base Earnings + Positive Adjustments
        </div>
        <div className="pt-bg-num" style={{ color: "var(--pt-green)" }}>{money2(totalBeforeDeductions)}</div>
      </div>

      <SectionCard
        title="➖ Deductions"
        color="var(--pt-red)"
        items={partsItems}
        sign="-"
        totalOverride={totalDeductions}
        totalLabel="Total Deductions"
        editable={editing}
        onAdd={addToBucket(setSubtractions, "parts")}
        onRemove={removeFrom(setSubtractions, (x) => (x.category || "parts") === "parts")}
        onEditItem={editIn(setSubtractions, (x) => (x.category || "parts") === "parts")}
      >
        {editing ? (
          <>
            <div className="pt-sec-row">
              <span className="l">ATM</span>
              <span className="a" style={{ color: "var(--pt-red)" }}>
                − {money2(atmCombined)}
              </span>
            </div>
            <div className="pt-edit-item">
              <div style={{ flex: 1, color: "var(--pt-muted)" }}>ATM Cash Used This Week</div>
              <input
                className="amount"
                type="number"
                value={atmCashUsedNum === 0 ? "" : atmCashUsedNum}
                placeholder="0"
                onChange={(e) => onAtmCashUsedChange(e.target.value === "" ? 0 : Number(e.target.value))}
              />
            </div>
          </>
        ) : (
          <div
            className="pt-sec-row"
            onClick={() => {
              const advItems = [];
              const didntUseItems = [];
              const usedFromProfitItems = [];

              // ATM ADV and ATM DIDN'T USE per location come from the
              // authoritative saved breakdown: Liz's subtractions carry one row
              // per location tagged category "atmAdv" / "atmNeg", and their sum
              // is exactly ericExtra.atm (atmCashUsedNum). Read those directly —
              // do NOT re-derive from live sessions and scale to the total, as
              // that distorts every row.
              for (const it of lizSubtractions) {
                const amt = Number(it.amount) || 0;
                if (amt <= 0) continue;
                if (it.category === "atmAdv") {
                  advItems.push({
                    _id: it._id || `atm_adv_${it.locationName || advItems.length}`,
                    description: it.locationName || it.description || "ATM ADV",
                    amount: amt,
                    isManual: false,
                  });
                } else if (it.category === "atmNeg") {
                  didntUseItems.push({
                    _id: it._id || `atm_didnt_use_${it.locationName || didntUseItems.length}`,
                    description: it.locationName || it.description || "ATM didn't use",
                    amount: amt,
                    isManual: false,
                  });
                }
              }

              // Fallback: no saved per-location breakdown — derive from the live
              // session sums instead (raw, unscaled).
              if (advItems.length === 0 && didntUseItems.length === 0) {
                for (const loc of locations) {
                  const adv = Number(loc?.atm?.adv) || 0;
                  if (adv > 0) {
                    advItems.push({ _id: `atm_adv_${loc.name}`, description: loc.name, amount: adv, isManual: false });
                  }
                  const du = Number(loc?.atm?.didntUse) || 0;
                  if (du > 0) {
                    didntUseItems.push({ _id: `atm_didnt_use_${loc.name}`, description: loc.name, amount: du, isManual: false });
                  }
                }
              }

              // Last resort: only the combined scalar is known.
              if (
                advItems.length === 0 &&
                didntUseItems.length === 0 &&
                atmCashUsedNum > 0
              ) {
                didntUseItems.push({
                  _id: "atm_didnt_use_total",
                  description: "Total",
                  amount: atmCashUsedNum,
                  isManual: false,
                });
              }

              const hasAutoUsedFromProfit = atmAdvItems.some(
                (x) => !x.isManual && x.description === "ATM Used from Profit"
              );
              if (hasAutoUsedFromProfit) {
                for (const loc of locations) {
                  const amt = Number(loc?.delivery?.atmReceipts) || 0;
                  if (amt > 0) {
                    usedFromProfitItems.push({
                      _id: `atm_used_from_profit_${loc.name}`,
                      description: loc.name,
                      amount: amt,
                      isManual: false,
                    });
                  }
                }
              }

              const manualItems = atmAdvItems.filter((x) => x.isManual);

              const groups = [];
              if (advItems.length > 0) groups.push({ id: "adv", title: "ATM ADV", items: advItems });
              if (didntUseItems.length > 0) groups.push({ id: "didntUse", title: "ATM didn't use", items: didntUseItems });
              if (usedFromProfitItems.length > 0) groups.push({ id: "usedFromProfit", title: "ATM Used from Profit", items: usedFromProfitItems });
              if (manualItems.length > 0) groups.push({ id: "manual", title: "Other", items: manualItems });

              setBreakdown({
                title: "ATM breakdown",
                groups,
                sign: "-",
              });
            }}
            style={{ cursor: "pointer" }}
          >
            <span className="l">
              ATM
              <span style={{ color: "var(--pt-muted)", fontSize: 10, marginLeft: 6, textDecoration: "underline" }}>
                view breakdown
              </span>
            </span>
            <span className="a" style={{ color: "var(--pt-red)" }}>
              − {money2(atmCombined)}
            </span>
          </div>
        )}
      </SectionCard>

      <div className={`pt-final ${isNeg ? "neg" : "pos"}`}>
        <div className="lb">Net Total (Final)</div>
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, marginTop: 2 }}>
          Total Before Deductions − Total Deductions
        </div>
        <div className="big">{isNeg ? "−" : ""}{money2(finalAbs)}</div>
        {isNeg ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, color: "#fff" }}>
            <ErrorOutlineIcon style={{ fontSize: 18 }} />
            <span style={{ fontSize: 13, fontWeight: 600 }}>
              Eric owes Liz {money2(finalAbs)}
            </span>
          </div>
        ) : (
          <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: "#fff" }}>
            Liz owes Eric {money2(finalAbs)}
          </div>
        )}
      </div>

      <PaymentTracking
        payment={payment}
        roles={paymentRoles}
        busy={paymentBusy}
        error={paymentError}
        onReport={onReportPayment}
        onVerify={onVerifyPayment}
        onUndoVerify={onUndoVerifyPayment}
      />

      <BreakdownDialog
        open={!!breakdown}
        title={breakdown?.title || ""}
        items={breakdown?.items || []}
        groups={breakdown?.groups}
        sign={breakdown?.sign || "+"}
        onClose={() => setBreakdown(null)}
      />
    </>
  );
}

/* ─── Liz tab ──────────────────────────────────────────────── */

function LizTab({
  locations, additions, subtractions,
  setAdditions, setSubtractions,
  cashInHand, onCashInHandChange,
  ericAdditions = [], ericSubtractions = [],
  editing, saveNote,
}) {
  const accent = "var(--pt-blue)";
  const bg = "var(--pt-liz-bg)";
  const [breakdown, setBreakdown] = useState(null);

  const earnings = EarningsByLocation({ partnerName: "Liz", accent, locations });
  const baseEarnings = earnings.baseEarnings;

  // For display only: append "(locationName)" to non-manual additions so
  // each NDS-sourced row shows where it came from. Manual rows keep their
  // own description verbatim. Underlying `additions` (used for save) is
  // untouched.
  const additionsDisplay = useMemo(
    () =>
      additions.map((it) =>
        it.isManual || !it.locationName
          ? it
          : { ...it, description: `${it.description} (${it.locationName})` }
      ),
    [additions]
  );

  // subtractions carries category = "atmNeg" | "atmAdv" | "zelle".
  // A manual entry defaults to "atmNeg". The "ATM Didn't Use" section also
  // absorbs any legacy entries saved with category "zelle" whose description
  // matches the ATM didn't use pattern (collapseBucket -> "atmDidntUse"), so
  // pre-existing saved weeks render correctly without a migration.
  const isAtmDidntUse = (it) => collapseBucket(it.description) === "atmDidntUse";
  const atmNegItems = subtractions.filter(
    (x) => (x.category || "atmNeg") === "atmNeg" || (x.category === "zelle" && isAtmDidntUse(x)),
  );
  const atmAdvItems = subtractions.filter((x) => x.category === "atmAdv");
  const zelleItems = subtractions.filter((x) => x.category === "zelle" && !isAtmDidntUse(x));

  const addTotal = sum(additions, (x) => x.amount);
  const atmNegTotal = sum(atmNegItems, (x) => x.amount);
  const atmAdvTotal = sum(atmAdvItems, (x) => x.amount);
  const zelleTotal = sum(zelleItems, (x) => x.amount);
  // Liz's own subtotal (base + additions − subtractions). Matches
  // LizAccounting's `subtractionsTotal` used as "Liz Profit".
  const lizProfit = baseEarnings + addTotal - atmNegTotal - atmAdvTotal - zelleTotal;

  // Eric's "Profit" = his Sub-Total (pre-owes). Matches LizAccounting.ericSubTotal.
  const ericBase = sum(
    locations.filter((l) => (Number(l.netProfit) || 0) > 0),
    (l) => {
      const inv = (l.investors || []).find((i) => i.name === "Eric");
      return inv ? computeShare(l, inv) : 0;
    },
  );
  const ericAddTotal = sum(ericAdditions, (x) => x.amount);
  const ericPartsTotal = sum(
    ericSubtractions.filter((x) => (x.category || "parts") === "parts"),
    (x) => x.amount,
  );
  const ericAtmAdvTotal = sum(
    ericSubtractions.filter((x) => x.category === "atmAdv"),
    (x) => x.amount,
  );
  const ericProfit = ericBase + ericAddTotal - ericPartsTotal - ericAtmAdvTotal;

  const totalCash = lizProfit + ericProfit;
  const shortOver = (Number(cashInHand) || 0) - totalCash;

  const addToBucket = (setter, category) => (it) =>
    setter((arr) => [...arr, { ...it, category, _id: newItemId() }]);
  const removeFrom = (setter, filter) => (idxWithinFilter) =>
    setter((arr) => {
      const targets = arr.map((it, i) => ({ it, i })).filter(({ it }) => filter(it));
      const target = targets[idxWithinFilter];
      return target ? arr.filter((_, i) => i !== target.i) : arr;
    });
  const editIn = (setter, filter) => (idxWithinFilter, patch) =>
    setter((arr) => {
      const targets = arr.map((it, i) => ({ it, i })).filter(({ it }) => filter(it));
      const target = targets[idxWithinFilter];
      if (!target) return arr;
      return arr.map((it, i) => (i === target.i ? { ...it, ...patch } : it));
    });

  return (
    <>
      <div
        className="pt-lb"
        style={{ color: accent, marginBottom: 10, marginTop: 8 }}
      >
        Liz's Weekly Summary{' '}
        {editing && (
          <span style={{ color: 'var(--pt-teal-txt)', marginLeft: 6 }}>
            — editing
          </span>
        )}
      </div>
      {saveNote && (
        <div
          className={saveNote.kind === 'ok' ? 'pt-card' : 'pt-error'}
          style={{
            padding: '8px 12px',
            marginBottom: 10,
            color: saveNote.kind === 'ok' ? 'var(--pt-green)' : undefined,
          }}
        >
          {saveNote.text}
        </div>
      )}

      {earnings.node}

      <div className="pt-card" style={{ background: bg }}>
        <div className="pt-lb">Total Base Earnings</div>
        <div className="pt-bg-num" style={{ color: accent }}>
          {money2(baseEarnings)}
        </div>
      </div>

      <CombinableSectionCard
        title="➕ Positive"
        color="var(--pt-green)"
        items={additionsDisplay}
        sign="+"
        editable={editing}
        onAdd={addToBucket(setAdditions, 'positive')}
        onRemove={(idx) => setAdditions((a) => a.filter((_, i) => i !== idx))}
        onEditItem={(idx, patch) =>
          setAdditions((a) =>
            a.map((x, i) => (i === idx ? { ...x, ...patch } : x))
          )
        }
        onOpenBreakdown={setBreakdown}
      />

      <div className="pt-card" style={{ background: bg }}>
        <div className="pt-lb">Total Before Deductions (A)</div>
        <div style={{ fontSize: 11, color: 'var(--pt-muted)', marginTop: 2, marginBottom: 4 }}>
          Base Earnings + Positive Adjustments
        </div>
        <div className="pt-bg-num" style={{ color: accent }}>
          {money2(baseEarnings + addTotal)}
        </div>
      </div>

      <CombinableSectionCard
        title="🏧 ATM DIDN'T USE"
        color="var(--pt-red)"
        items={atmNegItems}
        sign="-"
        editable={editing}
        onAdd={addToBucket(setSubtractions, 'atmNeg')}
        onRemove={removeFrom(
          setSubtractions,
          (x) => (x.category || 'atmNeg') === 'atmNeg' || (x.category === 'zelle' && isAtmDidntUse(x)),
        )}
        onEditItem={editIn(
          setSubtractions,
          (x) => (x.category || 'atmNeg') === 'atmNeg' || (x.category === 'zelle' && isAtmDidntUse(x)),
        )}
        onOpenBreakdown={setBreakdown}
      />

      <CombinableSectionCard
        title="🏧 ATM Advances"
        color="var(--pt-orange)"
        items={atmAdvItems}
        sign="-"
        editable={editing}
        onAdd={addToBucket(setSubtractions, 'atmAdv')}
        onRemove={removeFrom(setSubtractions, (x) => x.category === 'atmAdv')}
        onEditItem={editIn(setSubtractions, (x) => x.category === 'atmAdv')}
        onOpenBreakdown={setBreakdown}
        collapseGroups={LIZ_ATM_ADV_GROUPS}
      />

      <CombinableSectionCard
        title="🏦 Zelles & Deductions"
        color="var(--pt-purple)"
        items={zelleItems}
        sign="-"
        editable={editing}
        onAdd={addToBucket(setSubtractions, 'zelle')}
        onRemove={removeFrom(setSubtractions, (x) => x.category === 'zelle')}
        onEditItem={editIn(setSubtractions, (x) => x.category === 'zelle')}
        onOpenBreakdown={setBreakdown}
        collapseGroups={LIZ_ZELLE_GROUPS}
      />

      {/* Weekly summary — mirrors the block at the end of GM Profits Liz panel */}
      <LizWeeklySummary
        lizProfit={lizProfit}
        ericProfit={ericProfit}
        cashExpected={totalCash}
        cashInHand={cashInHand}
        onCashInHandChange={onCashInHandChange}
        shortOver={shortOver}
        editing={editing}
      />

      <BreakdownDialog
        open={!!breakdown}
        title={breakdown?.title || ''}
        items={breakdown?.items || []}
        groups={breakdown?.groups}
        sign={breakdown?.sign || '+'}
        onClose={() => setBreakdown(null)}
      />
    </>
  );
}

// Default collapse groups for the Positive card (shared by Eric and Liz).
const DEFAULT_COLLAPSE_GROUPS = [
  {
    id: "donations",
    title: "Donations",
    breakdownTitle: "Donations by location",
    match: (it) => collapseBucket(it.description) === "donations",
  },
  {
    id: "sams",
    title: "Sam's delivery",
    breakdownTitle: "Sam's delivery by location",
    match: (it) => collapseBucket(it.description) === "sams",
  },
  {
    id: "atmDidntUse",
    title: "ATM didn't use",
    breakdownTitle: "ATM didn't use by location",
    match: (it) => collapseBucket(it.description) === "atmDidntUse",
  },
  {
    id: "system",
    title: "System",
    breakdownTitle: "System by location",
    match: (it) => collapseBucket(it.description) === "system",
  },
];

// A SectionCard variant that collapses NDS-sourced entries into one clickable
// row per group (in view mode). Click opens a BreakdownDialog with per-location
// rows. `collapseGroups` is an array of { id, title, breakdownTitle, match }
// entries; defaults to Donations + Sam's delivery for back-compat.
function CombinableSectionCard({
  title, color, items, sign, editable, onAdd, onRemove, onEditItem, onOpenBreakdown,
  collapseGroups = DEFAULT_COLLAPSE_GROUPS,
}) {
  const bucketed = {};
  const rest = [];
  const restIndex = [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const g = collapseGroups.find((grp) => grp.match(it));
    if (g) {
      if (!bucketed[g.id]) bucketed[g.id] = [];
      bucketed[g.id].push(it);
    } else {
      rest.push(it);
      restIndex.push(i);
    }
  }

  const viewItems = [...rest];
  for (const g of collapseGroups) {
    const list = bucketed[g.id];
    if (list && list.length > 0) {
      viewItems.push({
        _id: `__collapsed_${g.id}__`,
        description: g.title,
        amount: list.reduce((a, b) => a + (Number(b.amount) || 0), 0),
        isManual: false,
        _breakdown: list,
        _breakdownTitle: g.breakdownTitle || g.title,
      });
    }
  }

  const totalOverride = items.reduce((a, b) => a + (Number(b.amount) || 0), 0);

  return (
    <SectionCard
      title={title}
      color={color}
      items={viewItems}
      sign={sign}
      totalOverride={totalOverride}
      editable={editable}
      onAdd={onAdd}
      onRemove={
        editable && onRemove
          ? (idxInView) => {
              if (idxInView < restIndex.length) onRemove(restIndex[idxInView]);
            }
          : undefined
      }
      onEditItem={
        editable && onEditItem
          ? (idxInView, patch) => {
              if (idxInView < restIndex.length) onEditItem(restIndex[idxInView], patch);
            }
          : undefined
      }
      renderRowClick={(item) => {
        if (item._breakdown) {
          onOpenBreakdown({
            title: item._breakdownTitle || item.description,
            items: item._breakdown,
            sign,
          });
        }
      }}
    />
  );
}

// Matches the auto-generated "ATM ADV" rows pushed by buildBucketsFromDeliveries.
// Manual rows (user-added in edit mode) keep their own descriptions and stay
// individually visible.
const LIZ_ATM_ADV_GROUPS = [
  {
    id: "atmAdv",
    title: "ATM ADV",
    breakdownTitle: "ATM ADV by location",
    match: (it) => (it.description || "").trim().toLowerCase() === "atm adv",
  },
];

const LIZ_ZELLE_GROUPS = [
  {
    id: "zelle",
    title: "Zelle",
    breakdownTitle: "Zelle by location",
    match: (it) => (it.description || "").trim().toLowerCase() === "zelle",
  },
];

function LizWeeklySummary({
  lizProfit, ericProfit, cashExpected, cashInHand, onCashInHandChange, shortOver, editing,
}) {
  const isOver = shortOver >= 0;

  return (
    <div
      style={{
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid var(--pt-border)",
        boxShadow: "var(--pt-shadow-md)",
        marginBottom: 20,
        marginTop: 4,
        background: "var(--pt-card)",
      }}
    >
      {/* Dark navy header */}
      <div
        style={{
          background: "var(--pt-navy)",
          color: "#fff",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: "rgba(255,255,255,0.65)" }}>
            Weekly Summary
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>Liz · Week close-out</div>
        </div>
      </div>

      {/* Profit breakdown */}
      <div style={{ padding: "10px 20px 4px" }}>
        <SummaryLine label="Liz Profit" value={lizProfit} dotColor="var(--pt-blue)" />
        <SummaryLine label="Eric Profit" value={ericProfit} dotColor="var(--pt-green)" />
      </div>

      {/* Cash Expected row — emphasised */}
      <div
        style={{
          background: "#f8fafc",
          borderTop: "1px solid var(--pt-border)",
          borderBottom: "1px solid var(--pt-border)",
          padding: "14px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--pt-muted)" }}>
            Expected
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--pt-text)", marginTop: 2 }}>
            Cash Expected
          </div>
        </div>
        <div
          style={{
            fontSize: 22, fontWeight: 800, letterSpacing: "-0.3px",
            color: cashExpected < 0 ? "var(--pt-red)" : "var(--pt-text)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {cashExpected < 0 ? "−" : ""}{money2(Math.abs(cashExpected))}
        </div>
      </div>

      {/* Cash In Hand — editable when editing, display otherwise */}
      <div style={{ padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--pt-muted)" }}>
            Delivered
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--pt-text)", marginTop: 2 }}>
            Cash In Hand
          </div>
        </div>
        {editing ? (
          <div style={{ position: "relative", width: 180 }}>
            <span
              style={{
                position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                color: "var(--pt-muted)", fontSize: 15, fontWeight: 600,
              }}
            >
              $
            </span>
            <input
              type="number"
              value={cashInHand === 0 || cashInHand === "" ? "" : cashInHand}
              placeholder="0"
              onChange={(e) => onCashInHandChange(e.target.value === "" ? 0 : Number(e.target.value))}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "10px 12px 10px 26px",
                border: "1.5px solid var(--pt-teal)",
                borderRadius: 10,
                fontSize: 18, fontWeight: 700,
                color: "var(--pt-text)",
                background: "#fff",
                outline: "none", fontFamily: "inherit",
                fontVariantNumeric: "tabular-nums",
                textAlign: "right",
              }}
            />
          </div>
        ) : (
          <div
            style={{
              fontSize: 20, fontWeight: 800,
              color: "var(--pt-green)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {money2(cashInHand)}
          </div>
        )}
      </div>

      {/* Short/Over footer strip */}
      <div
        style={{
          background: isOver ? "#ecfdf5" : "#fef2f2",
          borderTop: `2px solid ${isOver ? "#a7f3d0" : "#fecaca"}`,
          padding: "14px 20px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 10, height: 10, borderRadius: 5, flexShrink: 0,
              background: isOver ? "var(--pt-green)" : "var(--pt-red)",
            }}
          />
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 1.2, textTransform: "uppercase", color: isOver ? "#047857" : "#b91c1c" }}>
              {isOver ? "Over by" : "Short by"}
            </div>
            <div style={{ fontSize: 11, color: "var(--pt-muted)", marginTop: 1 }}>
              Cash In Hand − Cash Expected
            </div>
          </div>
        </div>
        <div
          style={{
            fontSize: 22, fontWeight: 900,
            color: isOver ? "#047857" : "#b91c1c",
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            flexShrink: 0,
            marginLeft: 10,
          }}
        >
          {`${isOver ? "+" : "−"}${money2(Math.abs(shortOver))}`}
        </div>
      </div>
    </div>
  );
}

function SummaryLine({ label, value, dotColor }) {
  const v = Number(value) || 0;
  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 0",
        borderBottom: "1px dashed var(--pt-border)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 8, height: 8, borderRadius: 4,
            background: dotColor || "var(--pt-muted-2)",
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 14, color: "var(--pt-text-2)", fontWeight: 500 }}>
          {label}
        </span>
      </div>
      <span
        style={{
          fontSize: 15, fontWeight: 700,
          color: v < 0 ? "var(--pt-red)" : "var(--pt-text)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {v < 0 ? "−" : ""}{money2(Math.abs(v))}
      </span>
    </div>
  );
}

/* ─── Delivery tab ──────────────────────────────────────────── */

function DeliveryTab({ locations }) {
  const totalCash = sum(locations, (l) => l.delivery?.cash);
  const totalZelle = sum(locations, (l) => l.delivery?.managerZelle);
  const totalR = sum(locations, (l) => l.delivery?.atmReceipts);
  const totalAdv = sum(locations, (l) => l.delivery?.atmAdv);
  const grand = totalCash + totalZelle + totalR + totalAdv;

  return (
    <>
      <div className="pt-delivery-grid">
        <div className="pt-delivery-card green">
          <div className="lb">Total Cash</div>
          <div className="v">{money0(totalCash)}</div>
        </div>
        <div className="pt-delivery-card blue">
          <div className="lb">Total Zelle</div>
          <div className="v">{money0(totalZelle)}</div>
        </div>
        <div className="pt-delivery-card red">
          <div className="lb">ATM Receipts</div>
          <div className="v">{money0(totalR)}</div>
        </div>
        <div className="pt-delivery-card orange">
          <div className="lb">ATM Adv</div>
          <div className="v">{money0(totalAdv)}</div>
        </div>
      </div>

      <div className="pt-card" style={{ borderColor: "rgba(167,139,250,0.3)", background: "rgba(167,139,250,0.08)" }}>
        <div className="pt-lb" style={{ color: "var(--pt-purple)" }}>Total Delivered</div>
        <div className="pt-bg-num" style={{ color: "var(--pt-purple)" }}>{money0(grand)}</div>
      </div>

      <div className="pt-tbl-wrap">
        <div className="pt-tbl-scroll">
          <table className="pt-tbl">
            <thead>
              <tr>
                <th>Location</th><th>Cash</th><th>Zelle</th><th>ATM Rec</th><th>ATM Adv</th><th>Total</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((l) => {
                const d = l.delivery || {};
                const t = (Number(d.cash) || 0) + (Number(d.managerZelle) || 0) + (Number(d.atmReceipts) || 0) + (Number(d.atmAdv) || 0);
                return (
                  <tr key={l.name}>
                    <td>{l.name}</td>
                    <td>{money0(d.cash || 0)}</td>
                    <td>{money0(d.managerZelle || 0)}</td>
                    <td>{money0(d.atmReceipts || 0)}</td>
                    <td>{money0(d.atmAdv || 0)}</td>
                    <td style={{ fontWeight: 800 }}>{money0(t)}</td>
                  </tr>
                );
              })}
              <tr className="total">
                <td>TOTAL</td>
                <td>{money0(totalCash)}</td>
                <td>{money0(totalZelle)}</td>
                <td>{money0(totalR)}</td>
                <td>{money0(totalAdv)}</td>
                <td>{money0(grand)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

/* ─── Manager / Summary — shared lazy loading ───────────────── */

// Manager/partner percentages arrive either as a number (18) or a
// pre-formatted string ("18%"). Render consistently as "18%".
function pctLabel(p) {
  if (p == null || p === "") return "";
  const s = String(p).trim();
  return s.endsWith("%") ? s : `${s}%`;
}

// A week reconciles when what the location handed over equals what it owed:
//   Total Delivered === Adjusted Total
// When a location has no adjustments Nuve sends no adjustedTotal, and the
// adjusted total is simply the Owner Profit — so that's the figure we compare
// against. If adjustedTotal is missing *and* the location does have
// adjustments we can't know the expected figure, so it's left unflagged
// rather than guessed at.
// Small rounding drift is normal, so a location is only flagged once it is off
// by more than $5.
const DELIVERY_TOLERANCE = 5;

function expectedDeliveryTotal(ms) {
  const adj = ms.adjustments || {};
  if (adj.adjustedTotal != null) return Number(adj.adjustedTotal) || 0;
  const hasAdjustments =
    (adj.additions || []).length > 0 || (adj.deductions || []).length > 0;
  if (hasAdjustments) return null;
  const owner = ms.ownerProfit != null ? ms.ownerProfit : ms.remainingProfit;
  return owner != null ? Number(owner) || 0 : null;
}

function totalDeliveredOf(ms) {
  const d = ms.delivery || {};
  if (d.totalDelivered != null) return Number(d.totalDelivered) || 0;
  return (
    (Number(d.cash) || 0) +
    (Number(d.managerZelle) || 0) +
    (Number(d.atmReceipts) || 0) +
    (Number(d.atmAdv) || 0)
  );
}

// null when the location balances (or we can't tell); otherwise
// { expected, delivered, delta } with delta = expected − delivered.
function deliveryMismatch(ms) {
  if (!ms) return null;
  const expected = expectedDeliveryTotal(ms);
  if (expected == null) return null;
  const delivered = totalDeliveredOf(ms);
  const delta = expected - delivered;
  return Math.abs(delta) > DELIVERY_TOLERANCE ? { expected, delivered, delta } : null;
}

// Tooltip for a flagged card — the red background is the signal, this is just
// the detail for anyone who hovers.
function mismatchTitle(m) {
  if (!m) return undefined;
  return `Total Delivered (${money2(m.delivered)}) doesn't match Adjusted Total (${money2(
    m.expected,
  )}) — off by ${money2(Math.abs(m.delta))}`;
}

// A location carries manager data if Nuve returned a manager summary with a
// manager, partners, or a weekly net profit for it.
function hasManagerData(ms) {
  if (!ms) return false;
  return Boolean(
    ms.managerName ||
      (ms.manager && (ms.manager.name || ms.manager.share != null)) ||
      (ms.partners && ms.partners.length) ||
      ms.weeklyNetProfit != null,
  );
}

// Lazy, cached per-location weekly report. The Manager & Summary chips show a
// location list; tapping one fetches THAT location's live week report (manager
// distribution + per-day summary + breakdown) — the server runs reportView on
// demand, so the cost is one location per tap, not every location on load.
// Results are cached by name (re-opening is instant) and cleared on week change.
function useWeekReport(ctx, api) {
  const { year, month, week } = ctx;
  const month1 = MONTHS.indexOf(month) + 1;
  const [cache, setCache] = useState({});
  const [loadingName, setLoadingName] = useState(null);
  const [errorName, setErrorName] = useState(null);

  useEffect(() => {
    setCache({});
    setLoadingName(null);
    setErrorName(null);
  }, [year, month1, week]);

  const load = (locationName) => {
    if (!locationName || !week) return;
    if (cache[locationName] || loadingName === locationName) return;
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    setLoadingName(locationName);
    setErrorName(null);
    api.fetchLocationWeekReport(
      { locationName, fromDate: toISO(fromDate), toDate: toISO(toDate) },
      (res) => {
        setCache((c) => ({
          ...c,
          [locationName]: {
            managerSummary: res?.managerSummary || null,
            summary: Array.isArray(res?.summary) ? res.summary : [],
            employeeSummary: Array.isArray(res?.employeeSummary) ? res.employeeSummary : [],
            summaryBreakdown: res?.summaryBreakdown || null,
          },
        }));
        setLoadingName((n) => (n === locationName ? null : n));
      },
      () => {
        setErrorName(locationName);
        setLoadingName((n) => (n === locationName ? null : n));
      },
    );
  };

  return { cache, loadingName, errorName, load };
}

// Small "← All locations" back control shared by both detail views. It sticks
// under the pill row so it stays reachable while scrolling a long location.
function BackLink({ onClick }) {
  return (
    <div className="pt-backbar">
      <button className="pt-backlink" onClick={onClick}>
        ← All locations
      </button>
    </div>
  );
}

// The master list both chips start on: every visible location, tap to open.
function LocationPicker({ locations, onPick }) {
  const [query, setQuery] = useState("");

  const sorted = [...locations].sort(
    (a, b) => (Number(b.netProfit) || 0) - (Number(a.netProfit) || 0),
  );
  if (sorted.length === 0) {
    return <div className="pt-empty">No locations to display.</div>;
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? sorted.filter((l) => (l.name || "").toLowerCase().includes(q))
    : sorted;

  return (
    <>
      <div className="pt-lb" style={{ marginTop: 8 }}>Select a location</div>
      <input
        className="pt-picker-search"
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search locations…"
        aria-label="Search locations"
      />
      {filtered.length === 0 && (
        <div className="pt-empty">No locations match “{query}”.</div>
      )}
      {filtered.map((l) => {
        const profit = Number(l.netProfit) || 0;
        return (
          <button
            key={l.name}
            className={`pt-listbtn ${l.mismatch ? "is-mismatch" : ""}`}
            title={mismatchTitle(l.mismatch)}
            onClick={() => onPick(l.name)}
          >
            <span style={{ fontWeight: 600 }}>{l.name}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: profit > 0 ? "var(--pt-green)" : "var(--pt-muted-2)", fontWeight: 700 }}>
                {money2(profit)}
              </span>
              <span style={{ color: "var(--pt-muted-2)" }}>›</span>
            </span>
          </button>
        );
      })}
    </>
  );
}

/* ─── Manager tab ───────────────────────────────────────────── */

function ManagerTab({ locations, weekReport }) {
  const [selected, setSelected] = useState(null);

  if (!selected) {
    return (
      <LocationPicker
        locations={locations}
        onPick={(name) => {
          setSelected(name);
          weekReport.load(name);
          scrollPtToTop();
        }}
      />
    );
  }

  const data = weekReport.cache[selected];
  const isLoading = weekReport.loadingName === selected && !data;
  const isError = weekReport.errorName === selected && !data;
  const loc = locations.find((l) => l.name === selected) || { name: selected };

  return (
    <>
      <BackLink
        onClick={() => {
          setSelected(null);
          scrollPtToTop();
        }}
      />
      {isLoading && <Spinner label="Loading manager summary…" />}
      {isError && (
        <div className="pt-error">Couldn’t load {selected}. Go back and try again.</div>
      )}
      {data &&
        (hasManagerData(data.managerSummary) ? (
          <ManagerLocationCard loc={loc} ms={data.managerSummary} />
        ) : (
          <div className="pt-empty">No manager summary for {selected} this week.</div>
        ))}
    </>
  );
}

// One card per location, mirroring Nuve's "Profit Summary" — net profit,
// the manager/partner distribution, owner profit, any adjustments/other
// items, and the delivered total. All fields read straight from the
// Nuve-provided managerSummary object; nothing is recomputed here.
function ManagerLocationCard({ loc, ms }) {
  const manager = ms.manager || {};
  const partners = ms.partners || [];
  const ownerProfit = ms.ownerProfit != null ? ms.ownerProfit : ms.remainingProfit;
  const adj = ms.adjustments || {};
  const delivery = ms.delivery || {};
  const otherItems = ms.otherItems || [];
  const managerName = manager.name || ms.managerName || "—";

  const additions = adj.additions || [];
  const deductions = adj.deductions || [];
  const hasAdjustments =
    additions.length > 0 || deductions.length > 0 || adj.adjustedTotal != null;

  // How Was It Delivered? — the four delivery buckets + total.
  const dCash = Number(delivery.cash) || 0;
  const dZelle = Number(delivery.managerZelle) || 0;
  const dReceipts = Number(delivery.atmReceipts) || 0;
  const dAdv = Number(delivery.atmAdv) || 0;
  const deliveryTotal =
    delivery.totalDelivered != null
      ? Number(delivery.totalDelivered) || 0
      : dCash + dZelle + dReceipts + dAdv;

  // ATM Cash Flow — sum each transaction type. ATM Cash Total mirrors Nuve's
  // getATMTOTAL: ATM Cash Used + ATM Added From Profit.
  const atmTx = ms.atmTransactions || [];
  const atmAmt = (type) =>
    atmTx.filter((t) => t.type === type).reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const atmCashUsed = atmAmt("ATM_CASH");
  const atmFromProfit = atmAmt("ATM_FROM_PROFIT");
  const atmAdvTx = atmAmt("ATM_ADV");
  const atmDidntUse = atmAmt("ATM_DIDNT_USE");
  const atmCashTotal = atmCashUsed + atmFromProfit;
  const hasAtm = atmTx.length > 0 || ms.totalATMCash != null;

  // Delivered ≠ Adjusted Total → both halves of the equation go red.
  const mismatch = deliveryMismatch(ms);
  const mismatchClass = mismatch ? " is-mismatch" : "";
  const mismatchHint = mismatchTitle(mismatch);

  const row = (label, value, opts = {}) => (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: opts.border ? "8px 0 2px" : "6px 0",
        ...(opts.border ? { borderTop: "1px dashed var(--pt-border)", marginTop: 6 } : {}),
      }}
    >
      <span style={{ color: opts.muted ? "var(--pt-muted)" : "var(--pt-text-2)", fontSize: 13 }}>
        {label}
      </span>
      <b style={{ color: opts.color || "var(--pt-text)", fontSize: opts.big ? 16 : 14 }}>{value}</b>
    </div>
  );

  return (
    <>
      {/* Profit Summary */}
      <div className={`pt-card${mismatchClass}`} title={mismatchHint}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{loc.name}</div>

        {row("Weekly Net Profit", money2(ms.weeklyNetProfit || 0), { big: true })}

        <div className="pt-lb" style={{ marginTop: 12 }}>Distribution</div>
        <div className="pt-mgr">
          <span>
            Manager <b>{managerName}</b>
            {manager.percentage != null ? ` (${pctLabel(manager.percentage)})` : ""}
          </span>
          <b>{money2(manager.share || 0)}</b>
        </div>
        {partners.map((p, i) => (
          <div
            key={p.name || i}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "6px 12px",
              marginTop: 6,
              fontSize: 13,
              color: "var(--pt-text-2)",
            }}
          >
            <span>
              {p.name}
              {p.percentage != null ? ` (${pctLabel(p.percentage)})` : ""}
            </span>
            <b>{money2(p.share || 0)}</b>
          </div>
        ))}

        {row("Owner Profit", money2(ownerProfit || 0), {
          border: true,
          color: "var(--pt-green)",
          big: true,
        })}

        {hasAdjustments && (
          <>
            <div className="pt-lb" style={{ marginTop: 12 }}>Adjustments</div>
            {additions.map((a, i) => (
              <div
                key={`a${i}`}
                style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}
              >
                <span style={{ color: "var(--pt-muted)" }}>
                  + {a.description || a.assignedTo || "Addition"}
                </span>
                <b style={{ color: "var(--pt-green)" }}>{money2(a.amount || 0)}</b>
              </div>
            ))}
            {deductions.map((d, i) => (
              <div
                key={`d${i}`}
                style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}
              >
                <span style={{ color: "var(--pt-muted)" }}>
                  − {d.description || d.assignedTo || "Deduction"}
                </span>
                <b style={{ color: "var(--pt-red)" }}>{money2(d.amount || 0)}</b>
              </div>
            ))}
            {adj.adjustedTotal != null &&
              row("Adjusted Total", money2(adj.adjustedTotal), { border: true, big: true })}
          </>
        )}
      </div>

      {/* How Was It Delivered? */}
      <div className={`pt-card${mismatchClass}`} title={mismatchHint}>
        <div className="pt-lb">
          <span role="img" aria-label="delivery">📦</span> How Was It Delivered?
        </div>
        <div className="pt-delivery-grid" style={{ marginTop: 4 }}>
          <div className="pt-delivery-card green">
            <div className="lb">Cash</div>
            <div className="v">{money2(dCash)}</div>
          </div>
          <div className="pt-delivery-card blue">
            <div className="lb">Zelle</div>
            <div className="v">{money2(dZelle)}</div>
          </div>
          <div className="pt-delivery-card red">
            <div className="lb">ATM Receipts</div>
            <div className="v">{money2(dReceipts)}</div>
          </div>
          <div className="pt-delivery-card orange">
            <div className="lb">ATM ADV</div>
            <div className="v">{money2(dAdv)}</div>
          </div>
        </div>

        {otherItems.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div className="pt-lb" style={{ marginBottom: 4 }}>Other</div>
            {otherItems.map((o, i) => (
              <div
                key={`o${i}`}
                style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 12 }}
              >
                <span style={{ color: "var(--pt-muted)" }}>{o.description || o.assignedTo || "Item"}</span>
                <b>{money2(o.amount || 0)}</b>
              </div>
            ))}
          </div>
        )}

        {row("Total Delivered", money2(deliveryTotal), {
          border: true,
          color: "var(--pt-purple)",
          big: true,
        })}
      </div>

      {/* ATM Cash Flow */}
      {hasAtm && (
        <div className="pt-card">
          <div className="pt-lb">
            <span role="img" aria-label="atm">🏧</span> ATM Cash Flow
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 8,
              marginTop: 4,
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 10, color: "var(--pt-muted)", textTransform: "uppercase", letterSpacing: 1 }}>
                Manager
              </div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{managerName}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 10, color: "var(--pt-muted)", textTransform: "uppercase", letterSpacing: 1 }}>
                Weekly Starting Cash
              </div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>{money2(ms.totalATMCash || 0)}</div>
            </div>
          </div>
          <div className="pt-delivery-grid">
            <div className="pt-delivery-card red">
              <div className="lb">ATM Cash Used</div>
              <div className="v">{money2(atmCashUsed)}</div>
            </div>
            <div className="pt-delivery-card red">
              <div className="lb">ATM Added From Profit</div>
              <div className="v">{money2(atmFromProfit)}</div>
            </div>
            <div className="pt-delivery-card orange">
              <div className="lb">ATM Adv</div>
              <div className="v">{money2(atmAdvTx)}</div>
            </div>
            <div className="pt-delivery-card orange">
              <div className="lb">ATM Didn’t Use</div>
              <div className="v">{money2(atmDidntUse)}</div>
            </div>
          </div>
          {row("ATM Cash Total", money2(atmCashTotal), {
            border: true,
            color: "var(--pt-red)",
            big: true,
          })}
        </div>
      )}
    </>
  );
}

/* ─── Summary tab ───────────────────────────────────────────── */

// Percentages from Nuve arrive as numbers (possibly BigDecimal-encoded strings).
const pct2 = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `${n.toFixed(2)}%`;
};

// Accounting format: negatives wrapped in parentheses, e.g. ($1,275).
const acct = (v) => {
  const n = Number(v) || 0;
  const s = money0(Math.abs(n));
  return n < 0 ? `(${s})` : s;
};
// Net Profit: green when positive, red when negative (Nuve's positive/negative).
const netColor = (v) => {
  const n = Number(v) || 0;
  return n > 0 ? "var(--pt-green)" : n < 0 ? "var(--pt-red)" : undefined;
};
// Short/Over: red only when negative, default otherwise.
const soColor = (v) => ((Number(v) || 0) < 0 ? "var(--pt-red)" : undefined);

// The weekly summary is a per-location, per-day table (Nuve's Summary chip).
// Same master-detail flow as Manager: pick a location, fetch its live week
// report on demand, then show the daily table + breakdown.
function SummaryTab({ locations, weekReport }) {
  const [selected, setSelected] = useState(null);

  if (!selected) {
    return (
      <LocationPicker
        locations={locations}
        onPick={(name) => {
          setSelected(name);
          weekReport.load(name);
          scrollPtToTop();
        }}
      />
    );
  }

  const data = weekReport.cache[selected];
  const isLoading = weekReport.loadingName === selected && !data;
  const isError = weekReport.errorName === selected && !data;
  const rows = data?.summary || [];
  const empRows = data?.employeeSummary || [];

  return (
    <>
      <BackLink
        onClick={() => {
          setSelected(null);
          scrollPtToTop();
        }}
      />
      <div style={{ fontSize: 15, fontWeight: 700, margin: "2px 0 12px" }}>{selected}</div>
      {isLoading && <Spinner label="Loading summary…" />}
      {isError && (
        <div className="pt-error">Couldn’t load {selected}. Go back and try again.</div>
      )}
      {data &&
        (rows.length > 0 ? (
          <>
            <div className="pt-lb" style={{ marginTop: 4 }}>Daily Summary</div>
            <SummaryTable rows={rows} />

            {data.summaryBreakdown && <SummaryBreakdown b={data.summaryBreakdown} />}

            {empRows.length > 0 && (
              <>
                <div className="pt-lb" style={{ marginTop: 16 }}>Employees</div>
                <EmployeeSummaryTable rows={empRows} />
              </>
            )}
          </>
        ) : (
          <div className="pt-empty">No weekly summary for {selected} this week.</div>
        ))}
    </>
  );
}

// Day / Short-Over / Employee Names / Zelle / Receive By — Nuve's employee
// summary table. employeeNames is [{ name }]; receiveBy is a string[].
function EmployeeSummaryTable({ rows }) {
  return (
    <div className="pt-tbl-wrap">
      <div className="pt-tbl-scroll">
        <table className="pt-tbl">
          <thead>
            <tr>
              <th>Day</th><th>Short/Over</th><th>Employee Names</th><th>Zelle</th><th>Receive By</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.day || i}
                className={
                  r.day === "WEEK TOTAL"
                    ? `total${(Number(r.shortOver) || 0) < 0 ? " is-negative" : ""}`
                    : ""
                }
              >
                <td>{r.day}</td>
                <td style={{ color: netColor(r.shortOver) }}>{acct(r.shortOver)}</td>
                <td style={{ whiteSpace: "normal" }}>
                  {(r.employeeNames || []).map((e) => e?.name).filter(Boolean).join(", ")}
                </td>
                <td>{r.zelle != null ? money0(r.zelle) : ""}</td>
                <td style={{ whiteSpace: "normal" }}>{(r.receiveBy || []).join(", ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryTable({ rows }) {
  return (
    <div className="pt-tbl-wrap">
      <div className="pt-tbl-scroll">
        <table className="pt-tbl">
          <thead>
            <tr>
              <th>Day</th><th>Net Profit</th><th>Gross</th><th>Short/Over</th>
              <th>Loan In</th><th>Cust</th><th>Total In</th><th>Total Out</th>
              <th>JP Other In</th><th>JP Other Out</th><th>Match</th>
              <th>Match %</th><th>Payout %</th><th>Hold %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.day || i}
                className={
                  r.day === "WEEK TOTAL"
                    ? `total${(Number(r.netProfit) || 0) < 0 ? " is-negative" : ""}`
                    : ""
                }
              >
                <td>{r.day}</td>
                <td style={{ color: netColor(r.netProfit) }}>{acct(r.netProfit)}</td>
                <td>{money0(r.grossProfit || 0)}</td>
                <td style={{ color: soColor(r.shortOver) }}>{acct(r.shortOver)}</td>
                <td>{money0(r.loanIn || 0)}</td>
                <td>{Number(r.custCount) || 0}</td>
                <td>{money0(r.totalIn || 0)}</td>
                <td>{money0(r.totalOut || 0)}</td>
                <td>{money0(r.jpOtherIn || 0)}</td>
                <td>{money0(r.jpOtherOut || 0)}</td>
                <td>{money0(r.matchAmount || 0)}</td>
                <td>{pct2(r.matchPct)}</td>
                <td>{pct2(r.mchPayoutPct)}</td>
                <td>{pct2(r.mchHoldPct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryBreakdown({ b }) {
  const red = "var(--pt-red)";
  const num = (v) => Number(v) || 0;
  const money0Abs = (v) => money0(Math.abs(num(v))); // "$7,000" (no sign)
  const signColor = (v) => (num(v) < 0 ? red : "var(--pt-green)");

  const miniCard = (label, value, bg) => (
    <div
      style={{
        background: bg,
        color: "#fff",
        borderRadius: 8,
        padding: "10px 6px",
        textAlign: "center",
        lineHeight: 1.25,
      }}
    >
      <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 800, marginTop: 6 }}>{money0(value || 0)}</div>
    </div>
  );

  const op = { fontSize: 15, fontWeight: 700, color: "var(--pt-muted)", textAlign: "center" };
  const inset = { background: "var(--pt-card-alt)", borderRadius: 8 };
  const navyRow = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 0",
    borderBottom: "1px solid rgba(255,255,255,0.10)",
  };

  // Adjustment reduces profit: a positive adjustment shows red "– ($X)",
  // a negative one adds back and shows green "+ $X" (matches Nuve).
  const adj = num(b.adjustment);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginTop: 16 }}>
      <div className="pt-lb" style={{ marginBottom: -4 }}>Summary Breakdown</div>

      {/* Manager Loan to Bank */}
      <div className="pt-card">
        <div className="pt-lb">Manager Loan to Bank</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 14px 1fr 14px 1fr",
            gap: 6,
            alignItems: "center",
            marginTop: 4,
            marginBottom: 8,
          }}
        >
          {miniCard("Previous Week", b.previousWeekManagerLoan, "#c0392b")}
          <div style={op}>+</div>
          {miniCard("This Week", b.thisWeekManagerLoanIn, "#2471a3")}
          <div style={op}>=</div>
          {miniCard("Total", b.totalManagerLoan, "#1e8449")}
        </div>

        {/* Deductions */}
        <div style={{ borderTop: "1px solid var(--pt-border)", marginTop: 12, paddingTop: 12 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 13, color: "var(--pt-text)" }}>
              <b style={{ textTransform: "uppercase" }}>Deductions</b> (This Week)
            </span>
            <b style={{ color: red, fontWeight: 700 }}>- {money0Abs(b.thisWeekDeductions)}</b>
          </div>

          <div style={{ ...inset, padding: "10px 12px" }}>
            <div style={{ fontSize: 12, color: "var(--pt-blue)", fontWeight: 600, marginBottom: 6 }}>
              Applied to This Week First, then Previous Week
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 0",
                borderBottom: "1px solid var(--pt-border)",
                fontSize: 13,
              }}
            >
              <span style={{ color: "var(--pt-muted)" }}>
                From This Week ({money0(b.thisWeekManagerLoanIn)})
              </span>
              <b style={{ color: red, fontWeight: 600 }}>- {money0Abs(b.fromThisWeekDeduction)}</b>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 0 0",
                fontSize: 13,
              }}
            >
              <span style={{ color: "var(--pt-muted)" }}>
                From Previous Week ({money0(b.previousWeekManagerLoan)})
              </span>
              <b style={{ color: red, fontWeight: 600 }}>- {money0Abs(b.fromPreviousWeekDeduction)}</b>
            </div>
          </div>
        </div>

        {/* Remaining */}
        <div
          style={{
            ...inset,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 12,
            padding: 12,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "var(--pt-text)" }}>
            Remaining Manager Loan to Bank
          </span>
          <b style={{ color: signColor(b.remainingManagerLoan), fontWeight: 700 }}>
            {money0(b.remainingManagerLoan)}
          </b>
        </div>
      </div>

      {/* Total Net Profit — dark card */}
      <div style={{ background: "#1a2f5a", borderRadius: 16, padding: "4px 18px 16px" }}>
        <div style={navyRow}>
          <span style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}>Net Profit (This Week)</span>
          <b style={{ fontSize: 15, color: "#fff", fontWeight: 600 }}>{money0(b.weekNetProfit)}</b>
        </div>
        <div style={navyRow}>
          <span style={{ fontSize: 14, color: "rgba(255,255,255,0.85)" }}>Adjustments</span>
          <b style={{ fontSize: 15, fontWeight: 600, color: adj < 0 ? "#2ecc71" : "#e74c3c" }}>
            {adj < 0 ? `+ ${money0Abs(adj)}` : `– (${money0Abs(adj)})`}
          </b>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 500, color: "#fff" }}>Total Net Profit</span>
          <b style={{ fontSize: 22, fontWeight: 700, color: num(b.availableToApprove) < 0 ? "#e74c3c" : "#2ecc71" }}>
            {money0(b.availableToApprove)}
          </b>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ATM views (embedded tab + standalone detail screen)
   ══════════════════════════════════════════════════════════ */

const ATM_TABS = [
  { id: "summary", label: "Summary" },
  { id: "du", label: "Didn't Use" },
  { id: "lw", label: "Last Week" },
  { id: "nw", label: "Next Week" },
];

// Nuve reports a managerName on every location it summarises, whether or not
// that manager touched the ATM. Without this check a manager with no ATM
// transactions rides along as a "$0 $0 $0 $0" row on Next Week — and then
// shows up again on the FOLLOWING week's Last Week, since that's derived from
// this week's rows. A manager belongs on the sheet only once they've actually
// moved money (advEly counts: it's typed by hand, never derived).
const hasAtmActivity = (r) =>
  (Number(r.eric) || 0) !== 0 ||
  (Number(r.profit) || 0) !== 0 ||
  (Number(r.atmDidntUse) || 0) !== 0 ||
  (Number(r.advEly) || 0) !== 0;

// Which (year, month, weekNumber) precedes (y, m, wn)? Mirrors ATMReceipt.getPreviousWeek.
function previousWeekOf(year, month1, weekNumber) {
  if (weekNumber > 1) {
    return { year, month: month1, weekNumber: weekNumber - 1 };
  }
  const prevMonth = month1 === 1 ? 12 : month1 - 1;
  const prevYear = month1 === 1 ? year - 1 : year;
  const weeks = weeksInMonth(prevYear, prevMonth);
  if (!weeks.length) return null;
  return { year: prevYear, month: prevMonth, weekNumber: weeks[weeks.length - 1].weekNumber };
}

function useATMData(ctx, api, locationNames, archivedNames) {
  const { year, month, week } = ctx;
  const month1 = MONTHS.indexOf(month) + 1;
  // Same rule the weekly tabs use (see visibleLocations): an archived location
  // is hidden ONLY when it moved nothing this week. One that did still shows,
  // so history stays intact and no total changes. Because this is evaluated
  // against the live location list on every load, un-archiving brings a
  // location straight back — nothing is baked into the saved week.
  const archivedSet = useMemo(
    () => new Set((archivedNames || []).map((n) => (n || "").trim().toLowerCase())),
    [archivedNames],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState([]);
  const [autoRows, _setAutoRows] = useState([]);
  const [lastWeekRows, _setLastWeekRows] = useState([]);
  const [manualRows, _setManualRows] = useState([]);
  const [duRows, setDuRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // saveWeek is often invoked via setTimeout(saveWeek, 0) straight after a
  // setState (lock toggle, confirm edit, row delete). The scheduled callback is
  // the *previous* render's saveWeek, so reading the row state off the closure
  // serialises the pre-update values -- the UI would show a row LOCKED while the
  // payload still carried isLocked: false. These refs always hold the committed
  // rows, and saveWeek reads them instead of the closed-over state.
  const autoRowsRef = useRef([]);
  const lastWeekRowsRef = useRef([]);
  const manualRowsRef = useRef([]);

  const setAutoRows = useCallback((updater) => {
    _setAutoRows((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      autoRowsRef.current = next;
      return next;
    });
  }, []);
  const setLastWeekRows = useCallback((updater) => {
    _setLastWeekRows((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      lastWeekRowsRef.current = next;
      return next;
    });
  }, []);
  const setManualRows = useCallback((updater) => {
    _setManualRows((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      manualRowsRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    if (!week) return;
    setLoading(true);
    setError(null);

    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const prevWeek = previousWeekOf(year, month1, week.weekNumber);

    const sumTx = (txs, type) =>
      (txs || [])
        .filter((t) => t.type === type)
        .reduce((a, b) => a + (Number(b.amount) || 0), 0);

    // Promise wrappers around the callback-based apiRequest.
    const pCurrent = new Promise((resolve) => {
      api.fetchATMWeek(
        { year, month: month1, weekNumber: week.weekNumber },
        (res) => resolve(res?.week || null),
        () => resolve(null),
      );
    });
    const pPrev = prevWeek
      ? new Promise((resolve) => {
          api.fetchATMWeek(prevWeek, (res) => resolve(res?.week || null), () => resolve(null));
        })
      : Promise.resolve(null);
    const pLive = new Promise((resolve, reject) => {
      api.fetchLocationSummary(
        { fromDate, toDate, locationNames: locationNames || [] },
        (res) => resolve(res),
        (err) => reject(err),
      );
    });

    Promise.all([pCurrent, pPrev, pLive])
      .then(([savedWeek, prevWeekData, liveSummary]) => {
        const allSessions = liveSummary?.sessions || [];

        // Drop archived locations that had no ATM movement this week. Every
        // location is still fetched, so an archived one that DID move money
        // keeps its row and its share of the totals.
        const sessionHasAtmValue = (s) => {
          const tx = (s.managerSummary || {}).atmTransactions;
          return (
            sumTx(tx, "ATM_CASH") !== 0 ||
            sumTx(tx, "ATM_FROM_PROFIT") !== 0 ||
            sumTx(tx, "ATM_ADV") !== 0 ||
            sumTx(tx, "ATM_DIDNT_USE") !== 0
          );
        };
        const isArchivedName = (name) =>
          archivedSet.has((name || "").trim().toLowerCase());
        const sessions = allSessions.filter(
          (s) => !isArchivedName(s.locationName) || sessionHasAtmValue(s),
        );

        // --- Summary tab: normalise the saved [{location, summary}] shape
        // or derive {name, cash, gm} rows from the live sessions.
        const summaryRows = (() => {
          if (savedWeek?.atmSummary?.length) {
            return savedWeek.atmSummary.map((entry) => {
              if (entry?.summary && Array.isArray(entry.summary.atmTransactions)) {
                const tx = entry.summary.atmTransactions;
                return {
                  name: entry.location || entry.summary.managerName || "",
                  cash: sumTx(tx, "ATM_CASH"),
                  gm: sumTx(tx, "ATM_FROM_PROFIT"),
                };
              }
              return {
                name: entry?.name || entry?.location || "",
                cash: Number(entry?.cash) || 0,
                gm: Number(entry?.gm) || 0,
              };
            });
          }
          return sessions.map((s) => {
            const tx = s.managerSummary?.atmTransactions || [];
            return {
              name: s.locationName,
              cash: sumTx(tx, "ATM_CASH"),
              gm: sumTx(tx, "ATM_FROM_PROFIT"),
            };
          });
        })();
        // The saved-week branch above bypasses the session filter, so apply the
        // archived rule to the finished rows too (legacy saved weeks).
        setSummary(
          summaryRows.filter(
            (r) =>
              !isArchivedName(r.name) ||
              (Number(r.cash) || 0) !== 0 ||
              (Number(r.gm) || 0) !== 0,
          ),
        );

        // --- Didn't-Use rows: per-session ATM_DIDNT_USE totals (live).
        const du = [];
        for (const s of sessions) {
          const ms = s.managerSummary || {};
          const amt = sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
          if (amt > 0) du.push({ name: ms.managerName || s.locationName, amt });
        }
        setDuRows(du);

        // Managers who moved ATM money somewhere this week, per the LIVE
        // location summary. This — not a stored row's own numbers — is what
        // decides who belongs on Next Week: a saved row can be stale (zeros
        // written before the week's transactions landed), and dropping it on
        // that basis would hide a manager who is right there in the summary.
        const activeManagers = new Set();
        for (const s of sessions) {
          const ms = s.managerSummary || {};
          const manager = (ms.managerName || "").trim();
          if (!manager || manager === "undefined" || manager === "null") continue;
          const moved =
            sumTx(ms.atmTransactions, "ATM_CASH") ||
            sumTx(ms.atmTransactions, "ATM_ADV") ||
            sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
          if (moved) activeManagers.add(manager.toLowerCase());
        }
        const isActive = (r) => activeManagers.has((r.manager || "").trim().toLowerCase());

        // --- Next Week autoRows: saved if present, else derived fresh by
        // grouping LOCATION_SUMMARY sessions by manager (matches
        // ATMReceipt.getNextWeekAutoRows).
        const buildFreshAutoRows = () => {
          const map = {};
          for (const s of sessions) {
            const ms = s.managerSummary || {};
            const manager = (ms.managerName || "").trim();
            if (!manager || manager === "undefined" || manager === "null") continue;
            if (!map[manager]) {
              map[manager] = {
                id: newItemId(),
                manager,
                eric: 0,
                profit: 0,
                atmDidntUse: 0,
                advEly: 0,
              };
            }
            map[manager].eric += sumTx(ms.atmTransactions, "ATM_CASH");
            map[manager].profit += sumTx(ms.atmTransactions, "ATM_ADV");
            map[manager].atmDidntUse += sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
          }
          return Object.values(map).filter(hasAtmActivity);
        };
        setAutoRows(
          savedWeek?.autoRows?.length
            ? savedWeek.autoRows
                .map((r) => ({ ...r, id: r.id || newItemId() }))
                // Drop a saved row only when it is empty AND its manager has
                // no ATM records in this week's summary — i.e. a leftover row
                // for someone who simply didn't use the ATM. A zero row whose
                // manager IS active is stale, not empty: keep it (Refresh
                // fills in the real figures).
                .filter((r) => hasAtmActivity(r) || isActive(r))
            : buildFreshAutoRows(),
        );

        // --- manualNextWeekRows: only ever from saved state.
        setManualRows(
          (savedWeek?.manualNextWeekRows || []).map((r) => ({
            ...r,
            id: r.id || newItemId(),
          })),
        );

        // --- Last Week rows: derived from the previous week's autoRows +
        // manualNextWeekRows. atmLastWeek[manager] = Σ (eric + profit +
        // atmDidntUse) for that manager in the previous week. midWeekAdv is
        // preserved from the current week's saved lastWeekRows.
        const prevTotals = {};
        if (prevWeekData) {
          const allPrev = [
            ...(prevWeekData.autoRows || []),
            ...(prevWeekData.manualNextWeekRows || []),
          ];
          for (const r of allPrev) {
            const m = (r.manager || "").trim();
            if (!m || m === "undefined" || m === "null") continue;
            prevTotals[m] =
              (prevTotals[m] || 0) +
              (Number(r.eric) || 0) +
              (Number(r.profit) || 0) +
              (Number(r.atmDidntUse) || 0);
          }
        }
        const savedMid = savedWeek?.lastWeekRows || [];
        const lastWeekBuilt = Object.entries(prevTotals)
          .map(([manager, atmLastWeek]) => {
            const savedRow = savedMid.find(
              (r) => (r.manager || "").trim().toLowerCase() === manager.toLowerCase(),
            );
            const midWeekAdv = Number(savedRow?.midWeekAdv) || 0;
            const total = (Number(atmLastWeek) || 0) + midWeekAdv;
            return {
              manager,
              atmLastWeek: Number(atmLastWeek) || 0,
              midWeekAdv,
              total,
            };
          })
          // Nothing carried over and no mid-week advance typed in — the manager
          // has nothing to show here. (Covers previous weeks saved before the
          // Next Week filter existed.)
          .filter((r) => r.atmLastWeek !== 0 || r.midWeekAdv !== 0);
        setLastWeekRows(lastWeekBuilt);

        setLoading(false);
      })
      .catch((err) => {
        setError(typeof err === "string" ? err : "Failed to load ATM data");
        setLoading(false);
      });
    // setAutoRows / setLastWeekRows / setManualRows are useCallback([])-stable.
  }, [year, month1, week, api, locationNames, archivedSet, setAutoRows, setLastWeekRows, setManualRows]);

  const closeEdits = () => {
    setLastWeekRows((arr) => arr.map((r) => ({ ...r, isEditing: false, _original: undefined })));
    setAutoRows((arr) => arr.map((r) => ({ ...r, isEditing: false, _original: undefined })));
    setManualRows((arr) => arr.map((r) => ({ ...r, isEditing: false, _original: undefined })));
  };

  const saveWeek = () => {
    if (!week) return;
    closeEdits();
    setSaving(true);
    setFeedback(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    // Read the refs, not the closed-over state: saveWeek may be running from a
    // setTimeout scheduled by an older render (see the ref declarations above).
    const currentLastWeekRows = lastWeekRowsRef.current;
    const currentAutoRows = autoRowsRef.current;
    const currentManualRows = manualRowsRef.current;
    const payload = {
      year,
      month: month1,
      weekNumber: week.weekNumber,
      fromDate: toISO(fromDate),
      toDate: toISO(toDate),
      atmSummary: [],
      lastWeekRows: currentLastWeekRows.map((r) => ({
        manager: r.manager,
        atmLastWeek: Number(r.atmLastWeek) || 0,
        midWeekAdv: Number(r.midWeekAdv) || 0,
      })),
      autoRows: currentAutoRows.map((r) => ({
        id: r.id,
        manager: r.manager,
        eric: Number(r.eric) || 0,
        profit: Number(r.profit) || 0,
        advEly: Number(r.advEly) || 0,
        atmDidntUse: Number(r.atmDidntUse) || 0,
        isLocked: !!r.isLocked,
      })),
      manualNextWeekRows: currentManualRows.map((r) => ({
        id: r.id,
        manager: r.manager,
        eric: Number(r.eric) || 0,
        profit: Number(r.profit) || 0,
        advEly: Number(r.advEly) || 0,
        atmDidntUse: Number(r.atmDidntUse) || 0,
      })),
    };
    api.saveATMWeek(
      payload,
      () => {
        setSaving(false);
        setFeedback({ kind: "ok", text: "Saved" });
        setTimeout(() => setFeedback(null), 2500);
      },
      (err) => {
        setSaving(false);
        setFeedback({ kind: "err", text: typeof err === "string" ? err : "Save failed" });
      },
    );
  };

  const refresh = () => {
    if (!week) return;
    setRefreshing(true);
    setFeedback(null);
    const { fromDate, toDate } = weekRange(year, month1, week.weekNumber);
    const sumTx = (txs, type) =>
      (txs || [])
        .filter((t) => t.type === type)
        .reduce((a, b) => a + (Number(b.amount) || 0), 0);

    api.fetchLocationSummary(
      { fromDate, toDate, locationNames: locationNames || [] },
      (res) => {
        // Same archived rule as the initial load: hidden only when the
        // archived location moved nothing this week.
        const sessions = (res?.sessions || []).filter((s) => {
          if (!archivedSet.has((s.locationName || "").trim().toLowerCase())) return true;
          const tx = (s.managerSummary || {}).atmTransactions;
          return (
            sumTx(tx, "ATM_CASH") !== 0 ||
            sumTx(tx, "ATM_FROM_PROFIT") !== 0 ||
            sumTx(tx, "ATM_ADV") !== 0 ||
            sumTx(tx, "ATM_DIDNT_USE") !== 0
          );
        });

        // Rebuild fresh per-manager totals from live sessions.
        const freshMap = {};
        for (const s of sessions) {
          const ms = s.managerSummary || {};
          const manager = (ms.managerName || "").trim();
          if (!manager || manager === "undefined" || manager === "null") continue;
          if (!freshMap[manager]) {
            freshMap[manager] = {
              manager, eric: 0, profit: 0, atmDidntUse: 0, advEly: 0,
            };
          }
          freshMap[manager].eric += sumTx(ms.atmTransactions, "ATM_CASH");
          freshMap[manager].profit += sumTx(ms.atmTransactions, "ATM_ADV");
          freshMap[manager].atmDidntUse += sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
        }

        // Merge into existing autoRows: preserve advEly + rows currently in edit + locked rows.
        setAutoRows((prev) => {
          const merged = prev.map((r) => ({ ...r }));
          for (const fresh of Object.values(freshMap)) {
            if (merged.some((r) => r.manager === fresh.manager && r.isLocked)) continue;
            const idx = merged.findIndex((r) => r.manager === fresh.manager && !r.isEditing);
            if (idx !== -1) {
              merged[idx] = {
                ...merged[idx],
                eric: fresh.eric,
                profit: fresh.profit,
                atmDidntUse: fresh.atmDidntUse,
              };
            } else if (
              hasAtmActivity(fresh) &&
              !merged.some((r) => r.manager === fresh.manager)
            ) {
              // Only managers who actually moved money join the sheet. Rows
              // already on it are left alone — they may be edited or locked.
              merged.push({ id: newItemId(), isLocked: false, ...fresh });
            }
          }
          return merged;
        });

        // Also refresh Summary and Didn't Use from live sessions.
        setSummary(
          sessions.map((s) => {
            const tx = s.managerSummary?.atmTransactions || [];
            return {
              name: s.locationName,
              cash: sumTx(tx, "ATM_CASH"),
              gm: sumTx(tx, "ATM_FROM_PROFIT"),
            };
          }),
        );
        const du = [];
        for (const s of sessions) {
          const ms = s.managerSummary || {};
          const amt = sumTx(ms.atmTransactions, "ATM_DIDNT_USE");
          if (amt > 0) du.push({ name: ms.managerName || s.locationName, amt });
        }
        setDuRows(du);

        setRefreshing(false);
        setFeedback({ kind: "ok", text: "Refreshed" });
        setTimeout(() => setFeedback(null), 2500);
      },
      (err) => {
        setRefreshing(false);
        setFeedback({ kind: "err", text: typeof err === "string" ? err : "Refresh failed" });
      },
    );
  };

  return {
    loading, error,
    summary, autoRows, lastWeekRows, manualRows, duRows,
    setAutoRows, setLastWeekRows, setManualRows,
    saveWeek, refresh, saving, refreshing, feedback,
  };
}

function ATMContent({ data, isAdmin, hideInlineActions }) {
  const [tab, setTab] = useState("summary");
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);
  const {
    loading, error, summary, autoRows, lastWeekRows, manualRows, duRows,
    setAutoRows, setLastWeekRows, setManualRows,
    saveWeek, refresh, saving, refreshing, feedback,
  } = data;
  const canEdit = !!isAdmin;
  const showInlineActions = canEdit && !hideInlineActions;

  // ── Last Week row helpers ───────────────────────────────
  const toggleLastWeekEdit = (manager) =>
    setLastWeekRows((arr) =>
      arr.map((r) => r.manager === manager
        ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
        : r),
    );
  const updateLastWeekMidAdv = (manager, value) => {
    const v = value === "" ? 0 : Number(value) || 0;
    setLastWeekRows((arr) =>
      arr.map((r) => r.manager === manager
        ? { ...r, midWeekAdv: v, total: (Number(r.atmLastWeek) || 0) + v }
        : r),
    );
  };
  const cancelLastWeekEdit = (manager) =>
    setLastWeekRows((arr) =>
      arr.map((r) => r.manager === manager && r._original
        ? { ...r._original, isEditing: false, _original: undefined }
        : r),
    );
  const confirmLastWeekEdit = (manager) => {
    setLastWeekRows((arr) =>
      arr.map((r) => r.manager === manager ? { ...r, isEditing: false, _original: undefined } : r),
    );
    setTimeout(saveWeek, 0);
  };

  // ── Auto row helpers (Next Week) ────────────────────────
  const toggleAutoEdit = (id) =>
    setAutoRows((arr) =>
      arr.map((r) => r.id === id
        ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
        : r),
    );
  const updateAutoField = (id, field, value) => {
    const v = field === "manager" ? value : (value === "" ? 0 : Number(value) || 0);
    setAutoRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, [field]: v } : r),
    );
  };
  const cancelAutoEdit = (id) =>
    setAutoRows((arr) =>
      arr.map((r) => r.id === id && r._original
        ? { ...r._original, isEditing: false, _original: undefined }
        : r),
    );
  const confirmAutoEdit = (id) => {
    setAutoRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, isEditing: false, isLocked: true, _original: undefined } : r),
    );
    setTimeout(saveWeek, 0);
  };
  const toggleAutoLock = (id) => {
    setAutoRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, isLocked: !r.isLocked } : r),
    );
    setTimeout(saveWeek, 0);
  };

  // ── Manual row helpers (Next Week) ──────────────────────
  const addManualRow = () => {
    setManualRows((arr) => [
      ...arr,
      { id: newItemId(), manager: "", eric: 0, profit: 0, atmDidntUse: 0, advEly: 0, isEditing: true },
    ]);
  };
  const updateManualField = (id, field, value) => {
    const v = field === "manager" ? value : (value === "" ? 0 : Number(value) || 0);
    setManualRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, [field]: v } : r),
    );
  };
  const toggleManualEdit = (id) =>
    setManualRows((arr) =>
      arr.map((r) => r.id === id
        ? { ...r, isEditing: !r.isEditing, _original: r.isEditing ? undefined : { ...r } }
        : r),
    );
  const cancelManualEdit = (id) =>
    setManualRows((arr) => {
      const row = arr.find((r) => r.id === id);
      // new row that was never confirmed -> remove it entirely
      if (!row || !row._original) return arr.filter((r) => r.id !== id);
      return arr.map((r) => r.id === id
        ? { ...r._original, isEditing: false, _original: undefined }
        : r);
    });
  const confirmManualEdit = (id) => {
    setManualRows((arr) =>
      arr.map((r) => r.id === id ? { ...r, isEditing: false, _original: undefined } : r),
    );
    setTimeout(saveWeek, 0);
  };
  const removeManualRow = (id) => {
    setManualRows((arr) => arr.filter((r) => r.id !== id));
    setTimeout(saveWeek, 0);
  };

  const sc = sum(summary, (r) => r.cash);
  const sg = sum(summary, (r) => r.gm);
  const sTot = sc + sg;

  const la = sum(lastWeekRows, (r) => r.atmLastWeek);
  const lm = sum(lastWeekRows, (r) => r.midWeekAdv);
  const lTot = la + lm;

  const nextRows = [...autoRows, ...manualRows];
  // Per ATMReceipt source: row.eric = ATM_CASH, row.profit = ATM_ADV, row.atmDidntUse = ATM_DIDNT_USE.
  const nc = sum(nextRows, (r) => r.eric);
  const na = sum(nextRows, (r) => r.profit);
  const nd = sum(nextRows, (r) => r.atmDidntUse);
  const nTot = nc + na + nd;

  const dt = sum(duRows, (r) => r.amt);

  return (
    <>
      {/* Subtabs + action bar — Refresh + Save are admin-only. */}
      <div
        className={`pt-atm-subtabs${hideInlineActions ? " pt-atm-subtabs-embedded" : ""}`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, marginBottom: 6,
        }}
      >
        <div className="pt-subtabs" style={{ minWidth: 0 }}>
          {ATM_TABS.map((t) => (
            <button
              key={t.id}
              className={`pt-subtab amber ${tab === t.id ? "is-active" : ""}`}
              onClick={() => { setTab(t.id); scrollPtToTop(); }}
            >
              {t.label}
            </button>
          ))}
        </div>
        {showInlineActions && (
          <div className="pt-atm-actions" style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => setRefreshConfirmOpen(true)}
              disabled={refreshing || saving}
              style={atmGhostBtn}
              title="Pull latest data from location summary"
            >
              {refreshing ? "Refreshing…" : "↻ Refresh"}
            </button>
            <button
              onClick={saveWeek}
              disabled={saving || refreshing}
              style={atmPrimaryBtn}
              title="Save all ATM rows"
            >
              {saving ? "Saving…" : "💾 Save"}
            </button>
          </div>
        )}
      </div>

      {feedback && (
        <div
          className={feedback.kind === "ok" ? "pt-card" : "pt-error"}
          style={{
            padding: "8px 14px",
            marginBottom: 10,
            color: feedback.kind === "ok" ? "var(--pt-green)" : undefined,
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {feedback.text}
        </div>
      )}

      {error && <div className="pt-error">{error}</div>}
      {loading && <Spinner label="Loading ATM…" />}

      {!loading && tab === "summary" && (
        <>
          <div className="pt-hero amber">
            <div className="lb">Total ATM This Week</div>
            <div className="big">{money0(sTot)}</div>
            <div className="stats">
              <div className="stat"><div><div className="k">ATM Cash</div><div className="v">{money0(sc)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">GM Profit</div><div className="v">{money0(sg)}</div></div></div>
            </div>
          </div>
          <div className="pt-tbl-wrap">
            <div className="pt-tbl-scroll">
              <table className="pt-tbl">
                <thead><tr><th>ATM</th><th>ATM Cash</th><th>GM Profit</th><th>Total</th></tr></thead>
                <tbody>
                  {summary.map((r) => (
                    <tr key={r.name}>
                      <td>{r.name}</td>
                      <td>{money0(r.cash)}</td>
                      <td style={{ color: (Number(r.gm) || 0) > 0 ? "var(--pt-green)" : "var(--pt-muted-2)" }}>{money0(r.gm)}</td>
                      <td style={{ fontWeight: 800 }}>{money0((Number(r.cash) || 0) + (Number(r.gm) || 0))}</td>
                    </tr>
                  ))}
                  <tr className="total amber">
                    <td>TOTAL</td><td>{money0(sc)}</td><td>{money0(sg)}</td><td>{money0(sTot)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && tab === "lw" && (
        <>
          <div className="pt-hero blue">
            <div className="lb">ATM Last Week Total</div>
            <div className="big">{money0(lTot)}</div>
            <div className="stats">
              <div className="stat"><div><div className="k">ATM</div><div className="v">{money0(la)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">Mid Week ADV</div><div className="v">{money0(lm)}</div></div></div>
            </div>
          </div>
          <div className="pt-tbl-wrap">
            <div className="pt-tbl-scroll">
              <table className="pt-tbl">
                <thead>
                  <tr>
                    <th>Manager</th>
                    <th>ATM Last Wk</th>
                    <th>Mid Wk ADV</th>
                    <th>Total</th>
                    {canEdit && <th style={{ textAlign: "right", width: 90 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {lastWeekRows.map((r) => {
                    const mid = Number(r.midWeekAdv) || 0;
                    const tot = (Number(r.atmLastWeek) || 0) + mid;
                    return (
                      <tr key={r.manager}>
                        <td>{r.manager}</td>
                        <td>{money0(r.atmLastWeek)}</td>
                        <td style={{ color: mid > 0 ? "var(--pt-orange)" : "var(--pt-muted-2)" }}>
                          {canEdit && r.isEditing ? (
                            <NumInput
                              value={r.midWeekAdv}
                              onChange={(v) => updateLastWeekMidAdv(r.manager, v)}
                            />
                          ) : (
                            money0(mid)
                          )}
                        </td>
                        <td style={{ fontWeight: 800 }}>{money0(tot)}</td>
                        {canEdit && (
                          <td style={{ textAlign: "right" }}>
                            {r.isEditing ? (
                              <RowActionButtons
                                onCancel={() => cancelLastWeekEdit(r.manager)}
                                onConfirm={() => confirmLastWeekEdit(r.manager)}
                              />
                            ) : (
                              <IconRowBtn title="Edit" onClick={() => toggleLastWeekEdit(r.manager)}>✎</IconRowBtn>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  <tr className="total">
                    <td>TOTAL</td><td>{money0(la)}</td><td>{money0(lm)}</td><td>{money0(lTot)}</td>
                    {canEdit && <td />}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && tab === "nw" && (
        <>
          <div className="pt-hero green">
            <div className="lb">ATM Next Week Total</div>
            <div className="big">{money0(nTot)}</div>
            <div className="stats">
              <div className="stat"><div><div className="k">ATM Cash</div><div className="v">{money0(nc)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">ATM ADV</div><div className="v">{money0(na)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">Didn't Use</div><div className="v">{money0(nd)}</div></div></div>
            </div>
          </div>
          <div className="pt-tbl-wrap">
            <div className="pt-tbl-scroll">
              <table className="pt-tbl">
                <thead>
                  <tr>
                    <th>Manager</th>
                    <th>ATM Cash</th>
                    <th>ATM ADV</th>
                    <th>Didn't Use</th>
                    <th>Total</th>
                    {canEdit && <th style={{ textAlign: "right", width: 110 }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {autoRows.map((r) => (
                    <NextWeekRow
                      key={r.id}
                      row={r}
                      isManual={false}
                      canEdit={canEdit}
                      onUpdate={updateAutoField}
                      onToggleEdit={() => toggleAutoEdit(r.id)}
                      onCancel={() => cancelAutoEdit(r.id)}
                      onConfirm={() => confirmAutoEdit(r.id)}
                      onToggleLock={() => toggleAutoLock(r.id)}
                    />
                  ))}
                  {manualRows.map((r) => (
                    <NextWeekRow
                      key={r.id}
                      row={r}
                      isManual
                      canEdit={canEdit}
                      onUpdate={updateManualField}
                      onToggleEdit={() => toggleManualEdit(r.id)}
                      onCancel={() => cancelManualEdit(r.id)}
                      onConfirm={() => confirmManualEdit(r.id)}
                      onRemove={() => removeManualRow(r.id)}
                    />
                  ))}
                  <tr className="total">
                    <td>Total</td>
                    <td>{money0(nc)}</td>
                    <td>{money0(na)}</td>
                    <td>{money0(nd)}</td>
                    <td>{money0(nTot)}</td>
                    {canEdit && <td />}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {canEdit && (
            <button
              onClick={addManualRow}
              style={{
                marginTop: 10,
                background: "var(--pt-card)",
                border: "1.5px dashed var(--pt-teal)",
                color: "var(--pt-teal)",
                padding: "10px 14px",
                borderRadius: 10,
                fontWeight: 700,
                fontSize: 13,
                fontFamily: "inherit",
                cursor: "pointer",
                width: "100%",
              }}
            >
              + Add manual row
            </button>
          )}
        </>
      )}

      {!loading && tab === "du" && (
        <>
          <div className="pt-hero amber">
            <div className="lb">Total Didn't Use</div>
            <div className="big">{money0(dt)}</div>
            <div className="stats">
              <div className="stat"><div><div className="k">Last Week</div><div className="v">{money0(lTot)}</div></div></div>
              <div className="sep" />
              <div className="stat"><div><div className="k">Total Use</div><div className="v">{money0(lTot - dt)}</div></div></div>
            </div>
          </div>

          <div className="pt-card" style={{ padding: 0, overflow: "hidden" }}>
            {duRows.length === 0 ? (
              <div style={{ padding: 16, color: "var(--pt-muted-2)", fontSize: 13, textAlign: "center" }}>
                No unused ATM allocations this week.
              </div>
            ) : (
              duRows.map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid var(--pt-border)" }}>
                  <span style={{ color: "var(--pt-text-2)", fontSize: 13, fontWeight: 600 }}>{r.name}</span>
                  <span style={{ color: "var(--pt-amber-txt)", fontSize: 13, fontWeight: 700 }}>{money0(r.amt)}</span>
                </div>
              ))
            )}
          </div>

          {/* Summary footer — mirrors ATMReceipt's Total Didn't Use / Last Week / Total Use rows */}
          <div className="pt-card" style={{ padding: 0, overflow: "hidden", marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid var(--pt-border)", background: "#fff7ed" }}>
              <span style={{ color: "var(--pt-text)", fontSize: 13, fontWeight: 700 }}>Total Didn't Use</span>
              <span style={{ color: "var(--pt-amber-txt)", fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money0(dt)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "1px solid var(--pt-border)" }}>
              <span style={{ color: "var(--pt-text-2)", fontSize: 13, fontWeight: 600 }}>Last Week</span>
              <span style={{ color: "var(--pt-text)", fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{money0(lTot)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#ecfdf5" }}>
              <span style={{ color: "var(--pt-text)", fontSize: 13, fontWeight: 700 }}>Total Use</span>
              <span style={{ color: "var(--pt-green)", fontSize: 16, fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{money0(lTot - dt)}</span>
            </div>
          </div>
        </>
      )}

      {!hideInlineActions && (
        <ConfirmDialog
          open={refreshConfirmOpen}
          title="Refresh ATM Records"
          message="This will re-pull every row from the Manager Summary. Any in-progress edits on auto rows will be overwritten; manual rows and mid-week advances are preserved."
          confirmLabel={refreshing ? "Refreshing…" : "Refresh"}
          cancelLabel="Cancel"
          busy={refreshing}
          onClose={() => setRefreshConfirmOpen(false)}
          onConfirm={() => {
            setRefreshConfirmOpen(false);
            refresh();
          }}
        />
      )}
    </>
  );
}

// ─── ATM row helpers (shared by Last Week + Next Week edit UI) ───────────

const atmPrimaryBtn = {
  background: "var(--pt-teal)",
  border: "none",
  color: "#fff",
  padding: "8px 14px",
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 12,
  fontFamily: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
  boxShadow: "var(--pt-shadow-sm)",
};

const atmGhostBtn = {
  background: "var(--pt-card)",
  border: "1px solid var(--pt-border)",
  color: "var(--pt-text-2)",
  padding: "8px 12px",
  borderRadius: 10,
  fontWeight: 600,
  fontSize: 12,
  fontFamily: "inherit",
  cursor: "pointer",
  whiteSpace: "nowrap",
};

const numInputStyle = {
  width: 90,
  boxSizing: "border-box",
  padding: "6px 8px",
  border: "1px solid var(--pt-teal)",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  color: "var(--pt-text)",
  background: "#fff",
  outline: "none",
  textAlign: "right",
  fontFamily: "inherit",
  fontVariantNumeric: "tabular-nums",
};

function NumInput({ value, onChange }) {
  const display = value === 0 || value == null ? "" : value;
  return (
    <input
      type="number"
      value={display}
      placeholder="0"
      onChange={(e) => onChange(e.target.value)}
      style={numInputStyle}
    />
  );
}

function IconRowBtn({ title, onClick, danger, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      style={{
        background: danger ? "#fef2f2" : "var(--pt-card)",
        border: `1px solid ${danger ? "#fecaca" : "var(--pt-border)"}`,
        color: danger ? "var(--pt-red)" : "var(--pt-text-2)",
        width: 30, height: 30, borderRadius: 8,
        cursor: "pointer",
        fontSize: 14, fontWeight: 700,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        marginLeft: 4,
      }}
    >
      {children}
    </button>
  );
}

function RowActionButtons({ onCancel, onConfirm }) {
  return (
    <div style={{ display: "inline-flex", justifyContent: "flex-end" }}>
      <IconRowBtn title="Cancel" onClick={onCancel} danger>✕</IconRowBtn>
      <IconRowBtn title="Confirm" onClick={onConfirm}>✓</IconRowBtn>
    </div>
  );
}

function NextWeekRow({ row, isManual, canEdit, onUpdate, onToggleEdit, onCancel, onConfirm, onRemove, onToggleLock }) {
  const cash = Number(row.eric) || 0;
  const adv = Number(row.profit) || 0;
  const didnt = Number(row.atmDidntUse) || 0;
  const total = cash + adv + didnt;

  if (canEdit && row.isEditing) {
    return (
      <tr>
        <td>
          <input
            type="text"
            value={row.manager || ""}
            onChange={(e) => onUpdate(row.id, "manager", e.target.value)}
            placeholder="Manager"
            style={{ ...numInputStyle, width: 120, textAlign: "left" }}
          />
        </td>
        <td><NumInput value={row.eric} onChange={(v) => onUpdate(row.id, "eric", v)} /></td>
        <td><NumInput value={row.profit} onChange={(v) => onUpdate(row.id, "profit", v)} /></td>
        <td><NumInput value={row.atmDidntUse} onChange={(v) => onUpdate(row.id, "atmDidntUse", v)} /></td>
        <td style={{ fontWeight: 800 }}>{money0(total)}</td>
        <td style={{ textAlign: "right" }}>
          <RowActionButtons onCancel={onCancel} onConfirm={onConfirm} />
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        {row.manager || <span style={{ color: "var(--pt-muted-2)", fontStyle: "italic" }}>(unnamed)</span>}
        {isManual && (
          <span style={{
            background: "#fff7ed", color: "var(--pt-amber-txt)",
            fontSize: 9, fontWeight: 700, padding: "2px 6px",
            borderRadius: 4, marginLeft: 8, textTransform: "uppercase", letterSpacing: 0.5,
          }}>
            Manual
          </span>
        )}
      </td>
      <td>{money0(cash)}</td>
      <td style={{ color: adv > 0 ? "var(--pt-orange)" : "var(--pt-muted-2)" }}>{money0(adv)}</td>
      <td style={{ color: didnt > 0 ? "var(--pt-amber-txt)" : "var(--pt-muted-2)" }}>{money0(didnt)}</td>
      <td style={{ fontWeight: 800 }}>{money0(total)}</td>
      {canEdit && (
        <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
          <IconRowBtn title="Edit" onClick={onToggleEdit}>✎</IconRowBtn>
          {!isManual && onToggleLock && (
            <button
              onClick={onToggleLock}
              title={row.isLocked ? "LOCKED — click to unlock and allow refresh" : "UNLOCKED — click to lock and prevent refresh"}
              aria-label={row.isLocked ? "Locked" : "Unlocked"}
              style={{
                background: row.isLocked ? "#d32f2f" : "#e8f5e9",
                border: `1px solid ${row.isLocked ? "#b71c1c" : "#66bb6a"}`,
                color: row.isLocked ? "#fff" : "#2e7d32",
                height: 30,
                padding: "0 8px",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: 0.5,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                marginLeft: 4,
              }}
            >
              <span style={{ fontSize: 13 }}>{row.isLocked ? "🔒" : "🔓"}</span>
              {row.isLocked ? "LOCKED" : "UNLOCKED"}
            </button>
          )}
          {isManual && <IconRowBtn title="Remove" onClick={onRemove} danger>🗑</IconRowBtn>}
        </td>
      )}
    </tr>
  );
}

export function ATMDetail({ nav, ctx, api, locations, isAdmin }) {
  const { year, month, week } = ctx;
  // Fetch every location, archived included, then let the hook hide the
  // archived ones that had no ATM activity this week — an archived location
  // that DID move money still has to show, or the totals would shift.
  const locationNames = useMemo(() => (locations || []).map((l) => l.name), [locations]);
  const archivedNames = useMemo(
    () => (locations || []).filter((l) => l.archived).map((l) => l.name),
    [locations],
  );
  const data = useATMData(ctx, api, locationNames, archivedNames);
  return (
    <>
      <PTToolbar
        title={`ATM Records — ${week.id} · ${week.label}, ${year}`}
        onBack={() => nav("weeks", { year, month, mode: "atm" })}
      />
      <div className="pt-content">
        <ATMContent data={data} isAdmin={isAdmin} />
      </div>
    </>
  );
}
